import { CalendarIcon } from "@chakra-ui/icons";
import { ChronoUnit, LocalDate, LocalDateTime } from "@js-joda/core";
import {
  Badge,
  Box,
  Button,
  Checkbox,
  FormControl,
  FormLabel,
  Grid,
  HStack,
  Image,
  Input,
  InputGroup,
  InputRightElement,
  Menu,
  MenuButton,
  MenuItem,
  MenuList,
  Popover,
  PopoverBody,
  PopoverContent,
  PopoverTrigger,
  Select as ChakraSelect,
  Stack,
  Table,
  TableContainer,
  Tbody,
  Td,
  Text,
  Textarea,
  Th,
  Thead,
  Tr,
  VStack,
} from "@chakra-ui/react";
import { useQuery } from "@tanstack/react-query";
import React, { useCallback, useEffect, useMemo, useState } from "react";
import { auth } from "../../../../core/auth";
import { env } from "../../../../env";
import RangeDatePicker from "../../../../shared/components/DatePicker/RangeDatePicker";
import SharedSelect from "../../../../shared/components/Select";
import useApi from "../../../../shared/hooks/useApi";
import type { CaregiverId, ServiceCodeId } from "../../../../shared/schema/schema";
import { dateFormatter } from "../../../../shared/utils/date-formatter";
import {
  formatPlanOfCareDutyTimesPerDay,
  planOfCareDutyTimesPerDaySortValue,
  type PlanOfCareDutyTimesPerDayByDay,
} from "../../../../shared/utils/plan-of-care-duty-utils";
import EntityProfileActionBar from "../../../entityProfileModal/components/EntityProfileActionBar";
import EntityModalButton, {
  EntityModalButtonGroup,
} from "../../../entityProfileModal/components/EntityModalButton";
import PatientProfileEditModeButton from "../../../entityProfileModal/components/EntityProfileEditModeButton";
import EntityProfileField from "../../../entityProfileModal/components/EntityProfileField";
import { EntityProfileEmptyState } from "../../../entityProfileModal/components/EntityProfileFeedback";
import EntityProfileInlineEditableRow from "../../../entityProfileModal/components/EntityProfileInlineEditableRow";
import EntityProfileQuerySection from "../../../entityProfileModal/components/EntityProfileQuerySection";
import EntityProfileDataTable, {
  type EntityProfileDataTableColumn,
} from "../../../entityProfileModal/components/EntityProfileDataTable";
import EntityProfileFilterBar, {
  EntityProfileFilterBarItem,
} from "../../../entityProfileModal/components/EntityProfileFilterBar";
import {
  entityProfileModalBorders,
  entityProfileModalColors,
} from "../../../entityProfileModal/entityProfileModal.theme";
import type { PatientProfileBridgeActions } from "../../patientProfileModal.actions";
import type { PatientProfilePatient } from "../../patientProfileModal.types";
import { formatPatientProfileValue } from "../../patientProfileModal.utils";
import AddEditMedicationModal from "../../../patient/components/AddEditMedicationModal";
import MedicalDiagnosisCodeTable from "../../../patient/components/MedicalDiagnosisCodeTable";
import PatientTaskDocumentChangeRequestsTable from "../../../patient/components/PatientTaskDocumentChangeRequestsTable";
import PatientTaskDocumentRejectionsTable from "../../../patient/components/PatientTaskDocumentRejectionsTable";
import {
  getCurrentWeekStart,
  useAssignPatientBloodPressureDevice,
  usePatientBloodPressure,
  usePatientCertificationPeriods,
  useDeletePatientCertificationPeriod,
  useAnswerPatientDutySheet,
  usePatientDocuments,
  usePatientDutySheets,
  usePatientMedicationProfile,
  usePatientNursingQuestions,
  usePatientPhysicianAssociations,
  usePatientPlansOfCare,
  usePatientTaskHistory,
  usePatientUploadedDocuments,
  usePatientVbpReports,
  usePatientVitals,
  useSubmitPatientDutySheetNote,
  useUnassignPatientBloodPressureDevice,
  useUpdatePatientAdvancedDirective,
  type PatientAdvancedDirective,
  type PatientCertificationPeriod,
  type PatientDocumentsResponse,
  type PatientDutySheetsResponse,
  type PatientMedicationProfileRow,
  type PatientNursingQuestion,
  type PatientPhysicianAssociation,
  type PatientPlanOfCareResponse,
  type PatientVital,
  type PatientUploadedDocument,
  type PatientVbpReport,
  getPatientAllergiesLabel,
  usePatientMedicalInformationController,
  useInvalidatePatientMedicalQueries,
  type PatientMedicalInformationProfile,
  type PatientMedicalInformationField,
} from "./PatientProfileMedicalSection.hooks";

type Props = {
  actions: PatientProfileBridgeActions;
  advancedDirectiveProfile?: PatientMedicalAdvancedDirectiveProfile;
  medicalInformationProfile?: PatientMedicalInformationProfile;
  medicalSummaryProfile?: PatientMedicalSummaryProfile;
  patient?: PatientProfilePatient;
  patientId: PatientProfilePatient["id"];
  sectionId: string;
};
type PatientMedicalSummaryProfile = Pick<PatientProfilePatient, "startOfCare">;
type PatientMedicalAdvancedDirectiveProfile = {
  advancedDirective?: PatientAdvancedDirective | null;
};

type PlanOfCareDuty = PatientPlanOfCareResponse["plansOfCare"][number]["duties"][number];
type DutySheetPlanRow = PatientDutySheetsResponse["rows"][number];
type DutySheetPlanDuty = DutySheetPlanRow["planOfCare"]["duties"][number];
type DutySheetRow = DutySheetPlanRow["dutySheets"][number];
type PlanOfCareDutyRow = PlanOfCareDuty | DutySheetPlanDuty;
type PatientDocumentRow =
  | PatientDocumentsResponse["documents"][number]
  | PatientDocumentsResponse["scannedDocuments"][number];
type PatientVisitDocumentRow = Extract<
  PatientDocumentRow,
  {
    __typename: "PatientExpectedDocumentObjectVisit" | "PatientExpectedScannedDocumentObjectVisit";
  }
>;
type UploadedDocumentsSortKey = "caregiver" | "createdAt" | "documentType";
type BloodPressureSortKey =
  | "bloodPressure"
  | "doneAt"
  | "handTremor"
  | "irregularHeartbeat"
  | "pulse";
type VbpDayName =
  | "Monday"
  | "Tuesday"
  | "Wednesday"
  | "Thursday"
  | "Friday"
  | "Saturday"
  | "Sunday";

const PAYER_SOURCE_OPTIONS = ["Managed Long-Term Care", "Certified Home Health Agency", "Private"];
const SERVICE_TYPE_OPTIONS = ["PCA", "HHA"];
const PATIENT_DOCUMENT_STATUS_OPTIONS = [
  { value: "all", label: "All statuses" },
  { value: "APPROVED", label: "Approved" },
  { value: "CHANGE_REQUESTED", label: "Change requested" },
  { value: "COMPLETED", label: "Completed" },
  { value: "IN_PROGRESS", label: "In progress" },
  { value: "MISSING", label: "Missing" },
  { value: "OPEN_FOR_RESUBMISSION", label: "Open for re-submission" },
  { value: "SENT", label: "Sent" },
  { value: "SENT_TO_PHYSICIAN", label: "Sent to Physician" },
  { value: "SIGNED", label: "Signed" },
] as const;
const MEDICAL_INFORMATION_FIELDS: Array<{
  field: PatientMedicalInformationField;
  label: string;
}> = [
  { field: "aideFrequency", label: "Aide Frequency" },
  { field: "serviceType", label: "Service Type" },
  { field: "payerSource", label: "Current Payer Source" },
];

function ReadOnlyPlaceholder(props: { label: string; title?: string }) {
  return (
    <Box
      bg={entityProfileModalColors.surfaceMuted}
      border={entityProfileModalBorders.panel}
      borderRadius="2px"
      px={4}
      py={5}
    >
      {props.title ? (
        <Text color={entityProfileModalColors.text} fontSize="14px" fontWeight={700} mb={1}>
          {props.title}
        </Text>
      ) : null}
      <Text color={entityProfileModalColors.muted}>{props.label}</Text>
    </Box>
  );
}

function PatientProfileMedicalBridgeButton(props: {
  actions: PatientProfileBridgeActions;
  label: string;
  patient?: PatientProfilePatient;
  patientId: PatientProfilePatient["id"];
  sectionId: string;
  title: string;
}) {
  const refreshMedicalQueries = useInvalidatePatientMedicalQueries(props.patientId);
  const openBridge = () => {
    if (props.patient === undefined) {
      return;
    }

    props.actions.openLegacyPatientMedicalSection({
      data: {
        sectionId: props.sectionId,
        sectionTitle: props.title,
      },
      onDone: refreshMedicalQueries,
      patient: props.patient,
    });
  };

  return (
    <EntityModalButton
      disabled={props.patient === undefined}
      kind="blue"
      size="sm"
      onClick={openBridge}
    >
      {props.label}
    </EntityModalButton>
  );
}

function SectionActions(props: { children: React.ReactNode }) {
  return <EntityProfileActionBar>{props.children}</EntityProfileActionBar>;
}

function ActionHint(props: { children: React.ReactNode }) {
  return (
    <Text color={entityProfileModalColors.secondaryText} fontSize="13px">
      {props.children}
    </Text>
  );
}

function formatMedicalAnswer(value: unknown) {
  if (Array.isArray(value)) {
    if (
      value.length === 2 &&
      value.every((item) => item !== null && item !== undefined && item !== "")
    ) {
      return value.map((item) => String(item)).join(" / ");
    }

    return value.map((item) => String(item)).join(", ");
  }

  return formatPatientProfileValue(value);
}

function parseMedicalLocalDateTime(value: string | null) {
  if (value === null || value.trim() === "") {
    return null;
  }

  try {
    return LocalDateTime.parse(value.replace("Z", ""));
  } catch {
    return null;
  }
}

function formatMedicalDate(value: string | null) {
  const dateTime = parseMedicalLocalDateTime(value);

  return dateTime === null ? formatPatientProfileValue(value) : dateFormatter.toDate(dateTime);
}

function formatMedicalTime(value: string | null) {
  const dateTime = parseMedicalLocalDateTime(value);

  return dateTime === null ? "" : dateFormatter.toTime(dateTime);
}

function getMedicalDateSortValue(value: string | null) {
  return getSortValue(parseMedicalLocalDateTime(value)?.toString() ?? value);
}

function isMedicalDateInRange(
  value: string | null,
  startDate: LocalDate | null,
  endDate: LocalDate | null
) {
  const date = parseMedicalLocalDateTime(value)?.toLocalDate();

  if (date === undefined || date === null) {
    return startDate === null && endDate === null;
  }

  if (startDate !== null && date.isBefore(startDate)) {
    return false;
  }

  if (endDate !== null && date.isAfter(endDate)) {
    return false;
  }

  return true;
}

function getLatestMedicalAnswer(question: PatientNursingQuestion) {
  return [...(question.answers ?? [])].sort((first, second) => {
    const firstDate = first.updatedAt ?? first.createdAt ?? "";
    const secondDate = second.updatedAt ?? second.createdAt ?? "";

    return secondDate.localeCompare(firstDate);
  })[0];
}

function getCertificationPeriodStatus(period: PatientCertificationPeriod) {
  return period.isCompleted === true ? "Completed" : "Not Completed";
}

function parseCertificationPeriodDate(value: LocalDate | string | null | undefined) {
  if (value === null || value === undefined) {
    return null;
  }

  if (value instanceof LocalDate) {
    return value;
  }

  if (value.trim() === "") {
    return null;
  }

  try {
    const slashParts = value.split("/");

    if (slashParts.length === 3) {
      const [month, day, year] = slashParts;

      return LocalDate.of(Number(year), Number(month), Number(day));
    }

    return LocalDate.parse(value.slice(0, 10));
  } catch {
    return null;
  }
}

function formatCertificationPeriodDate(value: LocalDate | string | null | undefined) {
  try {
    const date = parseCertificationPeriodDate(value);

    return date === null ? formatPatientProfileValue(value) : dateFormatter.toDate(date);
  } catch {
    return formatPatientProfileValue(value);
  }
}

function getCertificationPeriodDays(period: PatientCertificationPeriod) {
  const startDate = parseCertificationPeriodDate(period.startDate);
  const endDate = parseCertificationPeriodDate(period.endDate);

  if (startDate === null || endDate === null) {
    return 0;
  }

  return startDate.until(endDate, ChronoUnit.DAYS);
}

function getCertificationPeriodTypeLabel(type: PatientCertificationPeriod["type"]) {
  if (type === "START_OF_CARE") {
    return "Start of Care";
  }

  if (type === "REASSESSMENT") {
    return "Re-assessment";
  }

  return type.replace(/_/g, " ");
}

function formatBloodPressureDoneAt(value: unknown) {
  if (value === null || value === undefined || String(value).trim() === "") {
    return "";
  }

  const rawValue = String(value);
  const date = new Date(rawValue);

  if (Number.isNaN(date.getTime())) {
    return formatPatientProfileValue(rawValue);
  }

  const pad = (part: number) => String(part).padStart(2, "0");

  return `${pad(date.getMonth() + 1)}/${pad(date.getDate())}/${date.getFullYear()} ${pad(
    date.getHours()
  )}:${pad(date.getMinutes())}:${pad(date.getSeconds())}`;
}

function getBloodPressureSortDate(value: unknown) {
  if (value === null || value === undefined) {
    return "";
  }

  const rawValue = String(value);
  const date = new Date(rawValue);

  return Number.isNaN(date.getTime()) ? rawValue : date.getTime();
}

function formatBloodPressureBoolean(value: boolean | null | undefined) {
  return value === true ? "Yes" : "No";
}

function getUploadedDocumentCaregiverId(document: PatientUploadedDocument) {
  if (document.caregiver === null || document.caregiver === undefined) {
    return undefined;
  }

  return typeof document.caregiver === "number" ? document.caregiver : document.caregiver.id;
}

function getUploadedDocumentCaregiverName(document: PatientUploadedDocument) {
  if (document.caregiver === null || document.caregiver === undefined) {
    return "";
  }

  if (typeof document.caregiver === "number") {
    return `#${document.caregiver}`;
  }

  return document.caregiver.displayName ?? `#${document.caregiver.id ?? ""}`;
}

function buildVbpWeek(startOfWeek: LocalDate): Array<{ date: LocalDate; name: VbpDayName }> {
  return ["Monday", "Tuesday", "Wednesday", "Thursday", "Friday", "Saturday", "Sunday"].map(
    (name, index) => ({ date: startOfWeek.plusDays(index), name: name as VbpDayName })
  );
}

function VbpRiskBadge(props: { riskLevel?: number | null }) {
  if (props.riskLevel === null || props.riskLevel === undefined || props.riskLevel <= 1) {
    return null;
  }

  return (
    <Badge
      alignItems="center"
      borderRadius="999px"
      colorScheme={props.riskLevel >= 3 ? "red" : "orange"}
      display="inline-flex"
      fontSize="14px"
      fontWeight={800}
      h="22px"
      justifyContent="center"
      lineHeight="1"
      minW="22px"
      mr={1}
      px={0}
      title={props.riskLevel >= 3 ? "Very urgent" : "Urgent"}
    >
      !
    </Badge>
  );
}

function VbpAnswerCell(props: { answer?: PatientVbpReport["answers"][number] }) {
  return (
    <VStack alignItems="stretch" minH="36px" spacing={1}>
      <Text fontSize="13px">
        <VbpRiskBadge riskLevel={props.answer?.riskLevel} />
        {formatPatientProfileValue(props.answer?.answer)}
      </Text>
      {props.answer?.notes ? (
        <Text color={entityProfileModalColors.muted} fontSize="12px">
          {props.answer.notes}
        </Text>
      ) : null}
    </VStack>
  );
}

function PatientProfileCertificationPeriodsSection(props: {
  actions: PatientProfileBridgeActions;
  patient?: PatientProfilePatient;
  patientId: PatientProfilePatient["id"];
  profile: PatientMedicalSummaryProfile;
}) {
  const periodsQuery = usePatientCertificationPeriods(props.patientId);
  const deleteCertificationPeriod = useDeletePatientCertificationPeriod(props.patientId);
  const [initialStartOfCare, setInitialStartOfCare] = React.useState(() =>
    formatCertificationPeriodDate(props.profile.startOfCare ?? LocalDate.now())
  );
  const periods = useMemo(
    () => periodsQuery.data?.certificationPeriods ?? [],
    [periodsQuery.data?.certificationPeriods]
  );
  const refreshCertificationPeriods = useCallback(
    () => void periodsQuery.refetch(),
    [periodsQuery]
  );
  const openCertificationPeriod = useCallback(
    (certificationPeriod?: PatientCertificationPeriod) => {
      if (props.patient === undefined) {
        return;
      }

      props.actions.openCertificationPeriod({
        data: {
          allCertificationPeriods: periods,
          certificationPeriod,
        },
        patient: props.patient,
        onDone: refreshCertificationPeriods,
      });
    },
    [periods, props.actions, props.patient, refreshCertificationPeriods]
  );
  const removeCertificationPeriod = useCallback(
    (period: PatientCertificationPeriod) => {
      const shouldDelete = window.confirm(
        "Are you sure you want to delete this certification period? If there's a related task that wasn't completed, it will be canceled."
      );

      if (!shouldDelete) {
        return;
      }

      deleteCertificationPeriod.mutate(period.id);
    },
    [deleteCertificationPeriod]
  );
  const createInitialCertificationPeriod = useCallback(() => {
    const parsedStartOfCare = parseCertificationPeriodDate(initialStartOfCare);

    if (parsedStartOfCare === null || props.patient === undefined) {
      return;
    }

    props.actions.openCertificationPeriod({
      data: {
        allCertificationPeriods: periods,
        certificationPeriod: {
          startDate: new Date(`${parsedStartOfCare.toString()}T00:00:00.000Z`),
          type: "START_OF_CARE",
        },
      },
      patient: props.patient,
      onDone: refreshCertificationPeriods,
    });
  }, [initialStartOfCare, periods, props.actions, props.patient, refreshCertificationPeriods]);
  const columns = useMemo<Array<EntityProfileDataTableColumn<PatientCertificationPeriod, string>>>(
    () => [
      {
        header: "From",
        key: "from",
        render: (period) => formatCertificationPeriodDate(period.startDate),
        sortKey: "from",
        sortValue: (period) => period.startDate,
      },
      {
        header: "To",
        key: "to",
        render: (period) => formatCertificationPeriodDate(period.endDate),
        sortKey: "to",
        sortValue: (period) => period.endDate,
      },
      {
        header: "",
        key: "days",
        render: (period) => `${getCertificationPeriodDays(period)} days`,
      },
      {
        header: "Status",
        key: "status",
        render: (period) => (
          <Badge bg="#e5e5e5" color={entityProfileModalColors.secondaryText} px={4} py={1}>
            {getCertificationPeriodStatus(period)}
          </Badge>
        ),
        sortKey: "status",
        sortValue: (period) => getCertificationPeriodStatus(period),
      },
      {
        cellProps: { textAlign: "right" },
        header: (
          <Box display="flex" justifyContent="flex-end">
            <EntityModalButton
              disabled={props.patient === undefined}
              iconClassName="fa fa-plus"
              kind="blue"
              size="sm"
              variant="outline"
              onClick={() => openCertificationPeriod()}
            >
              New period
            </EntityModalButton>
          </Box>
        ),
        headerAlign: "right",
        key: "actions",
        render: (period) => (
          <HStack justifyContent="flex-end" spacing={2}>
            <EntityModalButton
              aria-label={`Remove ${getCertificationPeriodTypeLabel(
                period.type
              )} certification period`}
              className="visible-on-parent-hover"
              disabled={deleteCertificationPeriod.isPending}
              iconClassName="fa fa-trash"
              kind="red"
              title="Remove"
              onClick={() => removeCertificationPeriod(period)}
            />
            <EntityModalButton
              aria-label={`Edit ${getCertificationPeriodTypeLabel(
                period.type
              )} certification period`}
              disabled={props.patient === undefined}
              iconClassName="fa fa-pencil"
              kind="white"
              title="Edit"
              onClick={() => openCertificationPeriod(period)}
            />
            <EntityModalButton
              className="d-flex justify-space-between align-items-center"
              disabled={props.patient === undefined}
              iconClassName="fa fa-external-link m-r-sm"
              kind="blue"
              size="sm"
              style={{ width: 130 }}
              onClick={() => openCertificationPeriod(period)}
            >
              <span className="flex-1">{getCertificationPeriodTypeLabel(period.type)}</span>
            </EntityModalButton>
          </HStack>
        ),
      },
    ],
    [
      deleteCertificationPeriod.isPending,
      openCertificationPeriod,
      props.patient,
      removeCertificationPeriod,
    ]
  );

  return (
    <EntityProfileQuerySection
      errorLabel="Could not load certification periods."
      isError={periodsQuery.isError}
      isLoading={periodsQuery.isLoading}
      loadingLabel="Loading certification periods"
      onRetry={() => void periodsQuery.refetch()}
    >
      <VStack alignItems="stretch" spacing={4}>
        <HStack alignItems="flex-start" flexWrap="wrap" justifyContent="space-between" spacing={4}>
          <Text color={entityProfileModalColors.title} fontSize="18px" fontWeight={700}>
            Certification periods
          </Text>
          <VStack alignItems="flex-end" spacing={3}>
            <Text color={entityProfileModalColors.title} fontSize="14px">
              Start of care: <b>{formatCertificationPeriodDate(props.profile.startOfCare)}</b>
            </Text>
          </VStack>
        </HStack>
        <EntityProfileDataTable
          columns={columns}
          getRowKey={(period) => period.id}
          rows={periods}
          showEmptyState={false}
        />
        {periods.length === 0 || props.profile.startOfCare === null ? (
          <Box className="start-of-care-form-wrapper">
            <Text color={entityProfileModalColors.text} fontSize="26px" lineHeight="1">
              <i aria-hidden="true" className="fa fa-exclamation-circle" />
            </Text>
            <Text color={entityProfileModalColors.text} fontSize="17px" fontWeight={700} mt={2}>
              It looks like this patient has no certification period
            </Text>
            <Text color={entityProfileModalColors.text} fontSize="14px" mt={1}>
              Fill in the initial start of care for the first period
            </Text>
            <InputGroup maxW="300px" mt={4} mx="auto" size="sm">
              <Input
                value={initialStartOfCare}
                onChange={(event) => setInitialStartOfCare(event.target.value)}
              />
              <InputRightElement pointerEvents="none">
                <CalendarIcon color={entityProfileModalColors.muted} />
              </InputRightElement>
              <EntityModalButton
                disabled={props.patient === undefined}
                kind="blue"
                size="sm"
                style={{ borderBottomLeftRadius: 0, borderTopLeftRadius: 0, flexShrink: 0 }}
                onClick={createInitialCertificationPeriod}
              >
                Create
              </EntityModalButton>
            </InputGroup>
          </Box>
        ) : null}
      </VStack>
    </EntityProfileQuerySection>
  );
}

function PatientProfileVitalsSection(props: {
  actions: PatientProfileBridgeActions;
  patient?: PatientProfilePatient;
  patientId: PatientProfilePatient["id"];
}) {
  const [measuredAtRange, setMeasuredAtRange] = React.useState<
    [LocalDate | null, LocalDate | null]
  >([null, null]);
  const vitalsQuery = usePatientVitals(props.patientId);
  const vitals = useMemo(
    () => vitalsQuery.data?.patientVitals ?? [],
    [vitalsQuery.data?.patientVitals]
  );
  const filteredVitals = useMemo(
    () =>
      vitals.filter((vital) =>
        isMedicalDateInRange(vital.vitalMeasuredAt, measuredAtRange[0], measuredAtRange[1])
      ),
    [measuredAtRange, vitals]
  );
  const refreshMedicalQueries = useInvalidatePatientMedicalQueries(props.patientId);
  const vitalColumns = useMemo<Array<EntityProfileDataTableColumn<PatientVital, string>>>(
    () => [
      {
        cellProps: { width: "44px" },
        header: "",
        key: "icon",
        render: () => (
          <Image alt="" h="30px" src="admin/images/icons/medical-heart-rate.svg" w="30px" />
        ),
      },
      {
        header: "Vital Sign",
        key: "question",
        render: (vital) => vital.questionText,
        sortKey: "question",
        sortValue: (vital) => vital.questionText,
      },
      {
        header: "Date Taken",
        key: "date",
        render: (vital) => formatMedicalDate(vital.vitalMeasuredAt),
        sortKey: "date",
        sortValue: (vital) => getMedicalDateSortValue(vital.vitalMeasuredAt),
      },
      {
        header: "Time Taken",
        key: "time",
        render: (vital) => formatMedicalTime(vital.vitalMeasuredAt),
        sortKey: "time",
        sortValue: (vital) => getMedicalDateSortValue(vital.vitalMeasuredAt),
      },
      {
        header: "Taken By",
        key: "takenBy",
        render: (vital) => formatPatientProfileValue(vital.reporterName),
        sortKey: "takenBy",
        sortValue: (vital) => vital.reporterName ?? "",
      },
      {
        header: "Value",
        key: "value",
        render: (vital) => formatMedicalAnswer(vital.answer),
        sortKey: "value",
        sortValue: (vital) => formatMedicalAnswer(vital.answer),
      },
      {
        header: "Units",
        key: "units",
        render: (vital) => formatPatientProfileValue(vital.vitalUnit),
        sortKey: "units",
        sortValue: (vital) => vital.vitalUnit ?? "",
      },
      {
        header: "Method",
        key: "method",
        render: (vital) => formatPatientProfileValue(vital.vitalMethod),
        sortKey: "method",
        sortValue: (vital) => vital.vitalMethod ?? "",
      },
      {
        header: "Comments",
        key: "comments",
        render: (vital) => formatPatientProfileValue(vital.vitalComments),
        sortKey: "comments",
        sortValue: (vital) => vital.vitalComments ?? "",
      },
    ],
    []
  );
  const handleMeasuredAtRangeChange = (date: [LocalDate, LocalDate | null] | null) => {
    setMeasuredAtRange(date === null ? [null, null] : [date[0], date[1]]);
  };

  return (
    <EntityProfileQuerySection
      errorLabel="Could not load visit measurements."
      isError={vitalsQuery.isError}
      isLoading={vitalsQuery.isLoading}
      loadingLabel="Loading visit measurements"
      onRetry={() => void vitalsQuery.refetch()}
    >
      <VStack alignItems="stretch" spacing={3}>
        <Text color={entityProfileModalColors.text} fontSize="18px" fontWeight={700}>
          Visit Measurements
        </Text>
        <Stack
          alignItems={{ base: "stretch", lg: "flex-end" }}
          direction={{ base: "column", lg: "row" }}
          justifyContent="space-between"
          spacing={3}
        >
          <Box>
            {vitals.length > 0 ? (
              <FormControl maxW={{ base: "100%", md: "300px" }}>
                <FormLabel
                  color={entityProfileModalColors.title}
                  fontSize="13px"
                  fontWeight={700}
                  mb={2}
                >
                  Date Taken
                </FormLabel>
                <InputGroup>
                  <RangeDatePicker
                    dateFormat="MM/dd/yyyy"
                    endDate={measuredAtRange[1]}
                    inputProps={{ h: "34px", fontSize: "14px", width: "full" }}
                    selected={measuredAtRange[1] ?? measuredAtRange[0]}
                    startDate={measuredAtRange[0]}
                    onChange={handleMeasuredAtRangeChange}
                  />
                  <InputRightElement h="34px">
                    <CalendarIcon color={entityProfileModalColors.muted} />
                  </InputRightElement>
                </InputGroup>
              </FormControl>
            ) : null}
          </Box>
          <SectionActions>
            <EntityModalButton
              disabled={props.patient === undefined}
              kind="blue"
              size="sm"
              onClick={() => {
                if (props.patient === undefined) {
                  return;
                }

                props.actions.openPatientMeasurement({
                  patient: props.patient,
                  onDone: refreshMedicalQueries,
                });
              }}
            >
              Add Measurement
            </EntityModalButton>
            <PatientProfileMedicalBridgeButton
              actions={props.actions}
              label="Export by dates"
              patient={props.patient}
              patientId={props.patientId}
              sectionId="medical-information"
              title="Medical Information"
            />
          </SectionActions>
        </Stack>
        {filteredVitals.length === 0 ? (
          <EntityProfileEmptyState>Nothing to show</EntityProfileEmptyState>
        ) : (
          <EntityProfileDataTable
            columns={vitalColumns}
            defaultPageSize={10}
            defaultSort={{ direction: "desc", key: "date" }}
            getRowKey={(vital, index) => `${vital.questionText}-${vital.vitalMeasuredAt ?? index}`}
            pageSizeOptions={[10, 25, 50, 100]}
            rows={filteredVitals}
          />
        )}
      </VStack>
    </EntityProfileQuerySection>
  );
}

function PatientProfileGeneralMedicalInformationSection(props: {
  patientId: PatientProfilePatient["id"];
}) {
  const questionsQuery = usePatientNursingQuestions(props.patientId);
  const questions = questionsQuery.data?.questions ?? [];
  const columns = useMemo<Array<EntityProfileDataTableColumn<PatientNursingQuestion, string>>>(
    () => [
      {
        header: "Question",
        key: "question",
        render: (question) => question.questionText,
        sortKey: "question",
        sortValue: (question) => question.questionText,
      },
      {
        header: "Answer",
        key: "answer",
        render: (question) => {
          const latestAnswer = getLatestMedicalAnswer(question);

          return formatMedicalAnswer(latestAnswer?.answerText);
        },
        sortKey: "answer",
        sortValue: (question) => {
          const latestAnswer = getLatestMedicalAnswer(question);

          return formatMedicalAnswer(latestAnswer?.answerText);
        },
      },
    ],
    []
  );

  return (
    <EntityProfileQuerySection
      errorLabel="Could not load general medical information."
      isError={questionsQuery.isError}
      isLoading={questionsQuery.isLoading}
      loadingLabel="Loading general medical information"
      onRetry={() => void questionsQuery.refetch()}
    >
      <VStack alignItems="stretch" spacing={3}>
        <Text color={entityProfileModalColors.text} fontSize="18px" fontWeight={700}>
          General Medical Information
        </Text>
        {questions.length === 0 ? (
          <EntityProfileEmptyState>Nothing to show</EntityProfileEmptyState>
        ) : (
          <EntityProfileDataTable
            columns={columns}
            defaultPageSize={10}
            defaultSort={{ direction: "asc", key: "question" }}
            getRowKey={(question) => question.id}
            pageSizeOptions={[10, 25, 50, 100]}
            rows={questions}
          />
        )}
      </VStack>
    </EntityProfileQuerySection>
  );
}

function PatientProfileUploadedDocumentsSection(props: {
  actions: PatientProfileBridgeActions;
  patientId: PatientProfilePatient["id"];
}) {
  const documentsQuery = usePatientUploadedDocuments(props.patientId);
  const documents = documentsQuery.data?.documents ?? [];
  const columns = useMemo<
    Array<EntityProfileDataTableColumn<PatientUploadedDocument, UploadedDocumentsSortKey>>
  >(
    () => [
      {
        header: "Document Type",
        key: "documentType",
        render: (document) => formatPatientProfileValue(document.docType?.name),
        sortKey: "documentType",
        sortValue: (document) => document.docType?.name ?? "",
      },
      {
        header: "Date",
        key: "date",
        render: (document) => getPatientDocumentDate(document.createdAt),
        sortKey: "createdAt",
        sortValue: (document) => getPatientDocumentDateValue(document.createdAt),
      },
      {
        header: "Caregiver",
        key: "caregiver",
        render: (document) => {
          const caregiverId = getUploadedDocumentCaregiverId(document);
          const caregiverName = getUploadedDocumentCaregiverName(document);

          if (caregiverId === undefined) {
            return caregiverName;
          }

          return (
            <EntityModalButton
              kind="blue"
              style={{ color: "#337ab7", fontSize: "14px", textDecoration: "underline" }}
              variant="link"
              onClick={() => props.actions.navigateToCaregiver(caregiverId as CaregiverId)}
            >
              {caregiverName}
            </EntityModalButton>
          );
        },
        sortKey: "caregiver",
        sortValue: getUploadedDocumentCaregiverName,
      },
      {
        header: "View Document",
        key: "viewDocument",
        render: (document) =>
          document.fileUrl ? (
            <EntityModalButton
              kind="blue"
              onClick={() => window.open(document.fileUrl ?? "", "_blank", "noopener")}
            >
              View document
            </EntityModalButton>
          ) : (
            ""
          ),
      },
    ],
    [props.actions]
  );

  return (
    <EntityProfileQuerySection
      errorLabel="Could not load uploaded documents."
      isError={documentsQuery.isError}
      isLoading={documentsQuery.isLoading}
      loadingLabel="Loading uploaded documents"
      onRetry={() => void documentsQuery.refetch()}
    >
      <EntityProfileDataTable
        columns={columns}
        defaultPageSize={10}
        getRowKey={(document, index) => document.id ?? document.uploadId ?? index}
        pageSizeOptions={[10, 25, 50, 100]}
        rows={documents}
        showEmptyState={false}
      />
    </EntityProfileQuerySection>
  );
}

function PatientProfileVbpSection(props: {
  actions: PatientProfileBridgeActions;
  patient?: PatientProfilePatient;
  patientId: PatientProfilePatient["id"];
}) {
  const [startOfWeek, setStartOfWeek] = React.useState(() => getCurrentWeekStart());
  const week = React.useMemo(() => buildVbpWeek(startOfWeek), [startOfWeek]);
  const endOfWeek = startOfWeek.plusDays(6);
  const questionsQuery = usePatientNursingQuestions(props.patientId);
  const reportsQuery = usePatientVbpReports(props.patientId, startOfWeek, endOfWeek);
  const isLoading = questionsQuery.isLoading || reportsQuery.isLoading;
  const isError = questionsQuery.isError || reportsQuery.isError;
  const questions = useMemo(
    () => (questionsQuery.data?.questions ?? []).filter((question) => question.isVBP),
    [questionsQuery.data?.questions]
  );
  const reports = useMemo(() => reportsQuery.data?.reports ?? [], [reportsQuery.data?.reports]);
  const answerByQuestionDate = useMemo(() => {
    const answers = new Map<string, PatientVbpReport["answers"][number]>();

    reports.forEach((report) => {
      report.answers.forEach((answer) => {
        answers.set(`${answer.nursingQuestionId}:${report.date}`, answer);
      });
    });

    return answers;
  }, [reports]);
  const columns = useMemo(
    (): Array<EntityProfileDataTableColumn<PatientNursingQuestion, string>> => [
      {
        header: "Question",
        cellProps: { width: "30%" },
        headerProps: { width: "30%" },
        key: "question",
        render: (question) => <Text fontWeight={700}>{question.questionText}</Text>,
      },
      ...week.map((day) => ({
        cellProps: { width: "10%" },
        header: day.name,
        headerProps: { width: "10%" },
        key: day.name,
        render: (question: PatientNursingQuestion) => (
          <VbpAnswerCell
            answer={answerByQuestionDate.get(`${question.id}:${day.date.toString()}`)}
          />
        ),
      })),
    ],
    [answerByQuestionDate, week]
  );

  return (
    <EntityProfileQuerySection
      errorLabel="Could not load VBP reports."
      isError={isError}
      isLoading={isLoading}
      loadingLabel="Loading VBP reports"
      onRetry={() => {
        void questionsQuery.refetch();
        void reportsQuery.refetch();
      }}
    >
      <VStack alignItems="stretch" spacing={4}>
        <HStack flexWrap="wrap" justifyContent="space-between" spacing={3}>
          <HStack spacing={4}>
            <HStack spacing={1}>
              <VbpRiskBadge riskLevel={2} />
              <Text fontSize="13px">Urgent</Text>
            </HStack>
            <HStack spacing={1}>
              <VbpRiskBadge riskLevel={3} />
              <Text fontSize="13px">Very urgent</Text>
            </HStack>
          </HStack>
          <Text color={entityProfileModalColors.text} fontSize="16px" fontWeight={700}>
            {formatPatientProfileValue(startOfWeek)} - {formatPatientProfileValue(endOfWeek)}
          </Text>
          <SectionActions>
            <EntityModalButtonGroup>
              <EntityModalButton
                aria-label="Previous week"
                iconClassName="fa fa-chevron-left"
                kind="white"
                size="sm"
                title="Previous week"
                onClick={() => setStartOfWeek((current) => current.minusWeeks(1))}
              />
              <EntityModalButton
                aria-label="Next week"
                iconClassName="fa fa-chevron-right"
                kind="white"
                size="sm"
                title="Next week"
                onClick={() => setStartOfWeek((current) => current.plusWeeks(1))}
              />
            </EntityModalButtonGroup>
            <EntityModalButton
              kind="white"
              size="sm"
              onClick={() => setStartOfWeek(getCurrentWeekStart())}
            >
              Today
            </EntityModalButton>
            <EntityModalButton
              disabled={props.patient === undefined}
              kind="blue"
              size="sm"
              onClick={() => {
                if (props.patient === undefined) {
                  return;
                }

                props.actions.openPatientVbpReport({
                  data: { questions },
                  onDone: () => reportsQuery.refetch(),
                  patient: props.patient,
                });
              }}
            >
              Add Report
            </EntityModalButton>
          </SectionActions>
        </HStack>
        {questions.length === 0 ? (
          <EntityProfileEmptyState>No VBP questions.</EntityProfileEmptyState>
        ) : (
          <EntityProfileDataTable
            columns={columns}
            defaultPageSize={15}
            getRowKey={(question) => question.id}
            rows={questions}
          />
        )}
      </VStack>
    </EntityProfileQuerySection>
  );
}

function PatientProfileMedicalBridgeOnlySection(props: {
  actions: PatientProfileBridgeActions;
  patient?: PatientProfilePatient;
  patientId: PatientProfilePatient["id"];
  sectionId: string;
  title: string;
}) {
  return (
    <VStack alignItems="stretch" spacing={3}>
      <SectionActions>
        <PatientProfileMedicalBridgeButton
          actions={props.actions}
          label="Open Angular workflow"
          patient={props.patient}
          patientId={props.patientId}
          sectionId={props.sectionId}
          title={props.title}
        />
      </SectionActions>
      <ActionHint>
        This subsection needs a React page surface. The action workflow is available in Angular.
      </ActionHint>
    </VStack>
  );
}

function getMedicalInformationDisplayValue(value: string | null) {
  return formatPatientProfileValue(value);
}

function PatientMedicalInformationInput(props: {
  field: PatientMedicalInformationField;
  isFCAgency: boolean;
  value: string;
  onChange: (value: string) => void;
}) {
  const selectOptions =
    props.field === "payerSource"
      ? PAYER_SOURCE_OPTIONS
      : props.field === "serviceType" && props.isFCAgency
      ? SERVICE_TYPE_OPTIONS
      : null;

  if (selectOptions !== null) {
    return (
      <ChakraSelect
        size="sm"
        value={props.value}
        onChange={(event) => props.onChange(event.target.value)}
      >
        <option value="">empty</option>
        {selectOptions.map((option) => (
          <option key={option} value={option}>
            {option}
          </option>
        ))}
      </ChakraSelect>
    );
  }

  return (
    <Input size="sm" value={props.value} onChange={(event) => props.onChange(event.target.value)} />
  );
}

function PatientProfileMedicalInformationSection(props: {
  actions: PatientProfileBridgeActions;
  patient?: PatientProfilePatient;
  patientId: PatientProfilePatient["id"];
  profile: PatientMedicalInformationProfile;
}) {
  const controller = usePatientMedicalInformationController(props.patientId, props.profile);
  const authState = auth.getAuthState();
  const isFCAgency = authState.type === "authenticated" && authState.data.agency.id === 6205;
  const displayDraft = controller.draft;
  const updateDraft = (field: PatientMedicalInformationField, value: string) => {
    controller.setDraft((currentDraft) =>
      currentDraft === null ? currentDraft : { ...currentDraft, [field]: value }
    );
  };

  return (
    <VStack alignItems="stretch" spacing={6}>
      <HStack justifyContent="flex-end">
        {controller.canEdit ? (
          <PatientProfileEditModeButton
            isEditing={controller.isEditing}
            label="Edit"
            onClick={controller.toggleEditing}
          />
        ) : null}
      </HStack>

      <VStack alignItems="stretch" spacing={0}>
        {MEDICAL_INFORMATION_FIELDS.map((fieldConfig) => (
          <EntityProfileInlineEditableRow
            key={fieldConfig.field}
            activeField={controller.activeField}
            field={fieldConfig.field}
            isEditable={controller.isEditing && !controller.isSaving}
            label={fieldConfig.label}
            value={getMedicalInformationDisplayValue(props.profile[fieldConfig.field])}
            onActivate={controller.activateField}
            onCancel={controller.cancelField}
            onCommit={() => controller.commitField(fieldConfig.field)}
          >
            <PatientMedicalInformationInput
              field={fieldConfig.field}
              isFCAgency={isFCAgency}
              value={displayDraft?.[fieldConfig.field] ?? ""}
              onChange={(value) => updateDraft(fieldConfig.field, value)}
            />
          </EntityProfileInlineEditableRow>
        ))}
      </VStack>

      <Box>
        <Text color={entityProfileModalColors.text} fontSize="18px" fontWeight={700} mb={2}>
          Medical Notes
        </Text>
        {controller.isEditing ? (
          <VStack alignItems="stretch" spacing={2}>
            <Text color={entityProfileModalColors.danger} fontSize="13px">
              ** Use this section to describe the patient information. This section will be only
              visible to the skilled / Non - Skilled assigned to work with the patient **
            </Text>
            <Textarea
              isDisabled={controller.isSaving}
              rows={5}
              value={displayDraft?.medicalNotes ?? ""}
              onBlur={() => controller.commitField("medicalNotes")}
              onChange={(event) => updateDraft("medicalNotes", event.target.value)}
            />
          </VStack>
        ) : (
          <Text color={entityProfileModalColors.text} fontSize="14px" whiteSpace="pre-wrap">
            {formatPatientProfileValue(props.profile.medicalNotes)}
          </Text>
        )}
      </Box>

      <Box>
        <Text color={entityProfileModalColors.text} fontSize="18px" fontWeight={700} mb={2}>
          Allergies
        </Text>
        {controller.isEditing ? (
          <Box maxW="320px">
            <SharedSelect
              isDisabled={controller.isSaving}
              isLoading={controller.isAllergyOptionsLoading}
              label="Select allergies"
              multiple={true}
              options={controller.allergyOptions}
              searchable={true}
              selectedLabel={(value) =>
                value.length === 0 ? "Select allergies" : `Allergies (${value.length})`
              }
              value={displayDraft?.allergyIds ?? []}
              onChange={controller.commitAllergies}
            />
          </Box>
        ) : (
          <Text color={entityProfileModalColors.text} fontSize="14px">
            {getPatientAllergiesLabel(props.profile.allergies)}
          </Text>
        )}
      </Box>

      <PatientProfileVitalsSection
        actions={props.actions}
        patient={props.patient}
        patientId={props.patientId}
      />
      <PatientProfileGeneralMedicalInformationSection patientId={props.patientId} />
    </VStack>
  );
}

function getPatientAdvancedDirective(patient: PatientMedicalAdvancedDirectiveProfile) {
  return patient.advancedDirective ?? null;
}

const EMPTY_ADVANCED_DIRECTIVE: PatientAdvancedDirective = {
  hasDNR: null,
  hasProxy: null,
  firstName: null,
  lastName: null,
  phoneNumber: null,
};

function createAdvancedDirectiveDraft(
  patient: PatientMedicalAdvancedDirectiveProfile
): PatientAdvancedDirective {
  const advancedDirective = getPatientAdvancedDirective(patient);

  return {
    hasDNR: advancedDirective?.hasDNR ?? null,
    hasProxy: advancedDirective?.hasProxy ?? null,
    firstName: advancedDirective?.firstName ?? null,
    lastName: advancedDirective?.lastName ?? null,
    phoneNumber: advancedDirective?.phoneNumber ?? null,
  };
}

function formatPhoneNumberForSave(phoneNumber: string | null) {
  const trimmed = phoneNumber?.trim() ?? "";

  if (trimmed === "") {
    return null;
  }

  const digits = trimmed.replace(/\D/g, "");

  return trimmed.startsWith("+1") ? `+${digits}` : `+1${digits}`;
}

function AdvancedDirectiveToggle(props: {
  isDisabled: boolean;
  label: string;
  value: boolean | null;
  onChange: (value: boolean) => void;
}) {
  return (
    <VStack alignItems="stretch" spacing={2}>
      <Text color={entityProfileModalColors.labelText} fontSize="12px" fontWeight={700}>
        {props.label}
      </Text>
      <EntityModalButtonGroup>
        <EntityModalButton
          disabled={props.isDisabled}
          kind={props.value === true ? "blue" : "white"}
          size="sm"
          onClick={() => props.onChange(true)}
        >
          Yes
        </EntityModalButton>
        <EntityModalButton
          disabled={props.isDisabled}
          kind={props.value === false ? "blue" : "white"}
          size="sm"
          onClick={() => props.onChange(false)}
        >
          No
        </EntityModalButton>
      </EntityModalButtonGroup>
    </VStack>
  );
}

function PatientProfileAdvancedDirectivesSection(props: {
  patientId: PatientProfilePatient["id"];
  profile: PatientMedicalAdvancedDirectiveProfile;
}) {
  const [isEditing, setIsEditing] = React.useState(false);
  const [draft, setDraft] = React.useState<PatientAdvancedDirective>(() =>
    createAdvancedDirectiveDraft(props.profile)
  );
  const updateAdvancedDirectiveMutation = useUpdatePatientAdvancedDirective(props.patientId);

  React.useEffect(() => {
    if (!isEditing) {
      setDraft(createAdvancedDirectiveDraft(props.profile));
    }
  }, [isEditing, props.profile]);

  const advancedDirective = getPatientAdvancedDirective(props.profile);
  const displayValue = isEditing ? draft : advancedDirective ?? EMPTY_ADVANCED_DIRECTIVE;

  const saveDraft = () => {
    updateAdvancedDirectiveMutation.mutate(
      {
        ...draft,
        phoneNumber: formatPhoneNumberForSave(draft.phoneNumber),
      },
      {
        onSuccess: () => setIsEditing(false),
      }
    );
  };

  const updateDraft = <Key extends keyof PatientAdvancedDirective>(
    key: Key,
    value: PatientAdvancedDirective[Key]
  ) => {
    setDraft((current) => ({ ...current, [key]: value }));
  };

  return (
    <VStack alignItems="stretch" spacing={4}>
      <HStack justifyContent="flex-end">
        {isEditing ? (
          <>
            <EntityModalButton
              disabled={updateAdvancedDirectiveMutation.isPending}
              kind="white"
              size="sm"
              onClick={() => {
                setDraft(createAdvancedDirectiveDraft(props.profile));
                setIsEditing(false);
              }}
            >
              Cancel
            </EntityModalButton>
            <EntityModalButton
              isLoading={updateAdvancedDirectiveMutation.isPending}
              kind="blue"
              size="sm"
              onClick={saveDraft}
            >
              Save
            </EntityModalButton>
          </>
        ) : (
          <EntityModalButton kind="blue" size="sm" onClick={() => setIsEditing(true)}>
            Edit
          </EntityModalButton>
        )}
      </HStack>
      <Grid gap={4} templateColumns={{ base: "1fr", md: "repeat(2, minmax(0, 1fr))" }}>
        <AdvancedDirectiveToggle
          isDisabled={!isEditing}
          label="Has DNR"
          value={displayValue?.hasDNR ?? null}
          onChange={(value) => updateDraft("hasDNR", value)}
        />
        <AdvancedDirectiveToggle
          isDisabled={!isEditing}
          label="Has Proxy"
          value={displayValue?.hasProxy ?? null}
          onChange={(value) => updateDraft("hasProxy", value)}
        />
      </Grid>
      {displayValue?.hasProxy === true ? (
        <Grid gap={4} templateColumns={{ base: "1fr", md: "repeat(3, minmax(0, 1fr))" }}>
          <EntityProfileField
            label="Proxy First Name"
            value={
              isEditing ? (
                <Input
                  size="sm"
                  value={draft.firstName ?? ""}
                  onChange={(event) => updateDraft("firstName", event.target.value || null)}
                />
              ) : (
                formatPatientProfileValue(displayValue.firstName)
              )
            }
          />
          <EntityProfileField
            label="Proxy Last Name"
            value={
              isEditing ? (
                <Input
                  size="sm"
                  value={draft.lastName ?? ""}
                  onChange={(event) => updateDraft("lastName", event.target.value || null)}
                />
              ) : (
                formatPatientProfileValue(displayValue.lastName)
              )
            }
          />
          <EntityProfileField
            label="Proxy Phone Number"
            value={
              isEditing ? (
                <Input
                  size="sm"
                  value={draft.phoneNumber ?? ""}
                  onChange={(event) => updateDraft("phoneNumber", event.target.value || null)}
                />
              ) : (
                formatPatientProfileValue(displayValue.phoneNumber)
              )
            }
          />
        </Grid>
      ) : null}
    </VStack>
  );
}

type PlanOfCareSelectedDutySortKey =
  | "code"
  | "duration"
  | "frequency"
  | "fr"
  | "mo"
  | "name"
  | "notes"
  | "sa"
  | "section"
  | "serviceCodes"
  | "su"
  | "th"
  | "timesPerDay"
  | "tu"
  | "validatedOnly"
  | "we";

function formatPeriod(value: string | null | undefined) {
  return formatPatientProfileValue(value?.replace(/_/g, " "));
}

function formatPlanOfCareDate(value: LocalDate | string | null | undefined) {
  if (value === null || value === undefined || value === "") {
    return "";
  }

  if (value instanceof LocalDate) {
    return dateFormatter.toDate(value);
  }

  try {
    return dateFormatter.toDate(LocalDate.parse(value));
  } catch {
    return formatPatientProfileValue(value);
  }
}

function getSortValue(value: unknown) {
  return value === null || value === undefined ? "" : String(value);
}

function getPlanOfCareDutyDays(duty: PlanOfCareDutyRow) {
  return "daysOfWeek" in duty ? duty.daysOfWeek : duty.days;
}

function getPlanOfCareDutyNotes(duty: PlanOfCareDutyRow) {
  return "notes" in duty ? duty.notes : null;
}

function getPlanOfCareDutyDurationMinutes(duty: PlanOfCareDutyRow) {
  return "durationMinutes" in duty ? duty.durationMinutes : null;
}

function formatPlanOfCareDutyDuration(minutes: number | null) {
  return minutes === null ? "" : `${minutes} min`;
}

function getPlanOfCareDutySection(duty: PlanOfCareDutyRow) {
  return "sectionName" in duty.item ? duty.item.sectionName : null;
}

/** The item's agency-wide service code scope. Empty means every service code. */
function getPlanOfCareDutyServiceCodeIds(duty: PlanOfCareDutyRow): ServiceCodeId[] {
  return "serviceCodeIds" in duty.item ? duty.item.serviceCodeIds : [];
}

/** Occurrences per scheduled day; null means once. */
function getPlanOfCareDutyTimesPerDay(duty: PlanOfCareDutyRow): number | null {
  return "timesPerDay" in duty ? duty.timesPerDay : null;
}

/**
 * Occurrences on EACH day, for a duty performed more often on some of its days
 * than others. Null — the ordinary case — means `timesPerDay` describes them all.
 */
function getPlanOfCareDutyTimesPerDayByDay(
  duty: PlanOfCareDutyRow
): PlanOfCareDutyTimesPerDayByDay {
  return "timesPerDayByDay" in duty ? duty.timesPerDayByDay : null;
}

/** Whether only caregivers validated for the item see the duty on their visits. */
function isPlanOfCareDutyCaregiverRestricted(duty: PlanOfCareDutyRow): boolean {
  return "caregiverRestricted" in duty ? duty.caregiverRestricted : false;
}

/**
 * The codes an item is scoped to, from the agency's service code list. "All"
 * for an unscoped item, which applies to every service; an id the list does not
 * know is shown by number rather than dropped.
 */
function PlanOfCareDutyServiceCodes(props: {
  duty: PlanOfCareDutyRow;
  serviceCodesById: Map<ServiceCodeId, string>;
}) {
  const serviceCodeIds = getPlanOfCareDutyServiceCodeIds(props.duty);
  if (serviceCodeIds.length === 0) {
    return <Text color={entityProfileModalColors.muted}>All</Text>;
  }
  return (
    <Text>
      {serviceCodeIds
        .map((serviceCodeId) => props.serviceCodesById.get(serviceCodeId) ?? `#${serviceCodeId}`)
        .join(", ")}
    </Text>
  );
}

function getPlanOfCareDutyFrequency(duty: PlanOfCareDutyRow) {
  switch (duty.period) {
    case "AS_REQUESTED":
      return "As requested";
    case "EVERY_VISIT":
      return "Every visit";
    case "DAYS_A_WEEK":
      if (duty.frequency === null || duty.frequency === undefined) {
        return "";
      }
      // A weekly count with no days named, as a past service plan states many of its duties.
      return duty.visitsPerWeek === null || duty.visitsPerWeek === undefined
        ? `${duty.frequency}x week`
        : `${duty.frequency}/${duty.visitsPerWeek} days a week`;
    case "DAILY":
      return "Daily";
    case "WEEKLY":
      return "Weekly";
    case "1_2X_WEEKLY":
      return "1-2x Weekly";
    case "DAILY_AS_NEEDED":
      return "Daily (as needed)";
    case "WEEKLY_AS_NEEDED":
      return "Weekly (as needed)";
    case "1_2X_WEEKLY_AS_NEEDED":
      return "1-2x Weekly (as needed)";
    default:
      return formatPeriod(duty.period);
  }
}

function isPlanOfCareDutyRequiredOnDay(duty: PlanOfCareDutyRow, day: string) {
  return getPlanOfCareDutyDays(duty)?.includes(day) === true;
}

function PlanOfCareRequiredDay(props: { duty: PlanOfCareDutyRow; day: string }) {
  if (!isPlanOfCareDutyRequiredOnDay(props.duty, props.day)) {
    return null;
  }

  const checkboxId = `plan-of-care-duty-${props.duty.id}-${props.day.toLowerCase()}`;

  return (
    <Box className="d-flex justify-content-center">
      <div aria-label="Required" className="round-checkbox" role="img">
        <div className="round-checkbox-container">
          <input checked disabled readOnly id={checkboxId} type="checkbox" />
          <label htmlFor={checkboxId} />
        </div>
      </div>
    </Box>
  );
}

function SelectedPlanOfCareDutiesTable(props: { duties: PlanOfCareDutyRow[] }) {
  const { queries } = useApi();
  const serviceCodesQuery = useQuery(queries.serviceCode.list());
  const serviceCodesById = useMemo(
    () =>
      new Map<ServiceCodeId, string>(
        (serviceCodesQuery.data?.serviceCodes ?? []).map((serviceCode) => [
          serviceCode.id,
          serviceCode.code,
        ])
      ),
    [serviceCodesQuery.data]
  );

  const columns = useMemo<
    Array<EntityProfileDataTableColumn<PlanOfCareDutyRow, PlanOfCareSelectedDutySortKey>>
  >(
    () => [
      {
        header: "Duty",
        key: "name",
        render: (duty) => (
          <Text color={entityProfileModalColors.text} fontWeight={700}>
            {duty.item.label}
          </Text>
        ),
        sortKey: "name",
        sortValue: (duty) => duty.item.label,
      },
      {
        header: "Code",
        key: "code",
        render: (duty) => formatPatientProfileValue(duty.item.code),
        sortKey: "code",
        sortValue: (duty) => duty.item.code,
      },
      {
        header: "Section",
        key: "section",
        render: (duty) => formatPatientProfileValue(getPlanOfCareDutySection(duty)),
        sortKey: "section",
        sortValue: (duty) => getPlanOfCareDutySection(duty) ?? "",
      },
      {
        header: "Service codes",
        key: "serviceCodes",
        render: (duty) => (
          <PlanOfCareDutyServiceCodes duty={duty} serviceCodesById={serviceCodesById} />
        ),
        sortKey: "serviceCodes",
        sortValue: (duty) => getPlanOfCareDutyServiceCodeIds(duty).length,
      },
      {
        cellAlign: "center" as const,
        header: "Times/day",
        headerAlign: "center" as const,
        key: "timesPerDay",
        render: (duty) =>
          formatPlanOfCareDutyTimesPerDay(
            getPlanOfCareDutyTimesPerDay(duty),
            getPlanOfCareDutyTimesPerDayByDay(duty)
          ),
        sortKey: "timesPerDay",
        sortValue: (duty) =>
          planOfCareDutyTimesPerDaySortValue(
            getPlanOfCareDutyTimesPerDay(duty),
            getPlanOfCareDutyTimesPerDayByDay(duty)
          ),
      },
      {
        cellAlign: "center" as const,
        header: "Validated only",
        headerAlign: "center" as const,
        key: "validatedOnly",
        render: (duty) =>
          isPlanOfCareDutyCaregiverRestricted(duty) ? (
            <Text
              color={entityProfileModalColors.text}
              title="Only caregivers validated for this duty see it on their visits"
            >
              Yes
            </Text>
          ) : null,
        sortKey: "validatedOnly",
        sortValue: (duty) => (isPlanOfCareDutyCaregiverRestricted(duty) ? 1 : 0),
      },
      ...[
        ["MO", "MONDAY", "mo"],
        ["TU", "TUESDAY", "tu"],
        ["WE", "WEDNESDAY", "we"],
        ["TH", "THURSDAY", "th"],
        ["FR", "FRIDAY", "fr"],
        ["SA", "SATURDAY", "sa"],
        ["SU", "SUNDAY", "su"],
      ].map(([header, day, key]) => ({
        cellAlign: "center" as const,
        header,
        headerAlign: "center" as const,
        key,
        render: (duty: PlanOfCareDutyRow) => <PlanOfCareRequiredDay day={day} duty={duty} />,
        sortKey: key as PlanOfCareSelectedDutySortKey,
        sortValue: (duty: PlanOfCareDutyRow) => (isPlanOfCareDutyRequiredOnDay(duty, day) ? 1 : 0),
      })),
      {
        header: "Frequency",
        key: "frequency",
        render: (duty) => getPlanOfCareDutyFrequency(duty),
        sortKey: "frequency",
        sortValue: (duty) => getPlanOfCareDutyFrequency(duty),
      },
      {
        header: "Duration",
        key: "duration",
        render: (duty) => formatPlanOfCareDutyDuration(getPlanOfCareDutyDurationMinutes(duty)),
        sortKey: "duration",
        sortValue: (duty) => getPlanOfCareDutyDurationMinutes(duty) ?? 0,
      },
      {
        header: "Notes",
        key: "notes",
        render: (duty) => formatPatientProfileValue(getPlanOfCareDutyNotes(duty)),
        sortKey: "notes",
        sortValue: (duty) => getPlanOfCareDutyNotes(duty) ?? "",
      },
    ],
    [serviceCodesById]
  );

  return (
    <EntityProfileDataTable
      columns={columns}
      defaultPageSize={15}
      getRowKey={(duty) => duty.id}
      rows={props.duties}
      showEmptyState={false}
    />
  );
}

function PatientProfilePlanOfCareSection(props: {
  actions: PatientProfileBridgeActions;
  patient?: PatientProfilePatient;
  patientId: PatientProfilePatient["id"];
}) {
  const plansQuery = usePatientPlansOfCare(props.patientId);
  const plans = useMemo(() => plansQuery.data?.plansOfCare ?? [], [plansQuery.data?.plansOfCare]);
  const selectedPlan = plans[0] ?? null;
  const openPlanOfCareWorkflow = useCallback(
    (planOfCare?: PatientPlanOfCareResponse["plansOfCare"][number]) => {
      if (props.patient === undefined) {
        return;
      }

      props.actions.openPatientPlanOfCare({
        data: {
          selectedPlanOfCare: planOfCare,
        },
        onDone: () => void plansQuery.refetch(),
        patient: props.patient,
      });
    },
    [plansQuery, props.actions, props.patient]
  );
  const openPlanOfCareHistory = useCallback(() => {
    if (props.patient === undefined) {
      return;
    }

    props.actions.openPatientPlanOfCareHistory({
      patient: props.patient,
    });
  }, [props.actions, props.patient]);
  const openPlanPdf = useCallback(() => {
    if (selectedPlan?.pdfUrl) {
      window.open(selectedPlan.pdfUrl, "_blank", "noopener,noreferrer");
      return;
    }

    openPlanOfCareWorkflow(selectedPlan);
  }, [openPlanOfCareWorkflow, selectedPlan]);

  return (
    <EntityProfileQuerySection
      errorLabel="Could not load plans of care."
      isError={plansQuery.isError}
      isLoading={plansQuery.isLoading}
      loadingLabel="Loading plans of care"
      onRetry={() => void plansQuery.refetch()}
    >
      <VStack alignItems="stretch" spacing={4}>
        {selectedPlan === null ? (
          <SectionActions>
            <EntityModalButton
              disabled={props.patient === undefined}
              kind="blue"
              onClick={() => openPlanOfCareWorkflow()}
            >
              New plan of care
            </EntityModalButton>
          </SectionActions>
        ) : (
          <>
            <HStack alignItems="center" justifyContent="space-between" spacing={4}>
              <HStack flexWrap="wrap" spacing={2}>
                <EntityModalButton
                  disabled={props.patient === undefined}
                  kind="blue"
                  onClick={() => openPlanOfCareWorkflow(selectedPlan)}
                >
                  Create new plan of care
                </EntityModalButton>
                <EntityModalButton
                  disabled={props.patient === undefined}
                  kind="white"
                  onClick={openPlanOfCareHistory}
                >
                  View history
                </EntityModalButton>
                {selectedPlan.startDate || selectedPlan.endDate ? (
                  <Box className="inline-block label styled-label">
                    {selectedPlan.startDate ? formatPlanOfCareDate(selectedPlan.startDate) : null}
                    {selectedPlan.endDate
                      ? ` » ${formatPlanOfCareDate(selectedPlan.endDate)}`
                      : null}
                  </Box>
                ) : null}
              </HStack>
              <HStack flexWrap="wrap" justifyContent="flex-end" spacing={2}>
                <EntityModalButton kind="blue" variant="outline" onClick={openPlanPdf}>
                  View PDF
                </EntityModalButton>
                <EntityModalButton
                  disabled={props.patient === undefined}
                  kind="blue"
                  variant="outline"
                  onClick={() => openPlanOfCareWorkflow(selectedPlan)}
                >
                  Send PDF to e-mail
                </EntityModalButton>
              </HStack>
            </HStack>
            <SelectedPlanOfCareDutiesTable duties={selectedPlan.duties} />
          </>
        )}
      </VStack>
    </EntityProfileQuerySection>
  );
}

type DutySheetSelectedItem = DutySheetRow["selectedItems"][number];

type DutySheetCaregiverFilter = {
  caregiverId: DutySheetRow["caregiverId"];
  displayName: string;
  photoUrl: string | null;
};

type DutySheetCellEntry = {
  caregiverId: DutySheetRow["caregiverId"];
  caregiverName: string;
  caregiverPhotoUrl: string | null;
  selectedItem: DutySheetSelectedItem;
  visitInstanceId: DutySheetRow["visitInstanceId"];
};

function parseDutySheetDate(value: LocalDate | string) {
  return value instanceof LocalDate ? value : LocalDate.parse(String(value).slice(0, 10));
}

function getDutySheetWeekDays(startOfWeek: LocalDate) {
  const today = LocalDate.now();

  return Array.from({ length: 7 }, (_, index) => {
    const date = startOfWeek.plusDays(index);
    const dayLabel = date.isEqual(today)
      ? "Today"
      : `${date.dayOfWeek().name().slice(0, 2)} ${String(date.dayOfMonth()).padStart(2, "0")}`;

    return { date, dayLabel, key: date.toString() };
  });
}

function getDutySheetCaregiverName(sheet: DutySheetRow) {
  const displaySheet = sheet as DutySheetRow & {
    caregiverDisplayName?: string | null;
    caregiverName?: string | null;
  };

  return (
    displaySheet.caregiverDisplayName ??
    displaySheet.caregiverName ??
    `Caregiver ${sheet.caregiverId}`
  );
}

function getDutySheetCaregiverPhotoUrl(sheet: DutySheetRow) {
  const displaySheet = sheet as DutySheetRow & {
    caregiverPhotoUrl?: string | null;
  };

  return displaySheet.caregiverPhotoUrl ?? null;
}

function getDutySheetCaregiverFilters(row: DutySheetPlanRow): DutySheetCaregiverFilter[] {
  const caregivers = new Map<DutySheetRow["caregiverId"], DutySheetCaregiverFilter>();

  row.dutySheets.forEach((sheet) => {
    if (!caregivers.has(sheet.caregiverId)) {
      caregivers.set(sheet.caregiverId, {
        caregiverId: sheet.caregiverId,
        displayName: getDutySheetCaregiverName(sheet),
        photoUrl: getDutySheetCaregiverPhotoUrl(sheet),
      });
    }
  });

  return [...caregivers.values()].sort((a, b) => a.displayName.localeCompare(b.displayName));
}

function getDutySheetEntriesForDay(params: {
  day: LocalDate;
  duty: DutySheetPlanDuty;
  row: DutySheetPlanRow;
  selectedCaregiverIds: Set<DutySheetRow["caregiverId"]>;
}) {
  return (
    params.row.dutySheets
      .filter((sheet) => {
        if (!params.selectedCaregiverIds.has(sheet.caregiverId)) {
          return false;
        }

        return parseDutySheetDate(sheet.visitInstanceStartDate).isEqual(params.day);
      })
      // One entry per recorded OCCURRENCE. This used to `find` the first matching
      // item, so a duty recorded twice on a visit showed once and the second
      // occurrence -- with its own answer and note -- was invisible here.
      .flatMap((sheet) =>
        sheet.selectedItems
          .filter((item) => item.planOfCareItemId === params.duty.item.id)
          .map((selectedItem) => ({
            caregiverId: sheet.caregiverId,
            caregiverName: getDutySheetCaregiverName(sheet),
            caregiverPhotoUrl: getDutySheetCaregiverPhotoUrl(sheet),
            selectedItem,
            visitInstanceId: sheet.visitInstanceId,
          }))
      )
  );
}

/**
 * A key that separates two occurrences of one duty on one visit.
 *
 * `occurrenceIndex` is null on an aggregated plan-scheduled row, and two of those
 * must not collide as the string "null" -- so the index of the entry within its
 * sheet stands in when there is no occurrence to name.
 */
function dutySheetEntryKey(entry: DutySheetCellEntry, prefix?: string): string {
  const occurrence = entry.selectedItem.occurrenceIndex ?? "plan";

  return [
    ...(prefix === undefined ? [] : [prefix]),
    entry.visitInstanceId,
    entry.selectedItem.planOfCareItemId,
    occurrence,
  ].join("-");
}

function getDutySheetOperatorIcon(type: DutySheetSelectedItem["type"]) {
  switch (type) {
    case "MOBILE":
      return "admin/images/icons/caregiver.svg";
    case "IVR":
      return "admin/images/icons/call.svg";
    case "COORDINATOR":
      return "admin/images/icons/operator.svg";
    default:
      return null;
  }
}

function DutySheetLegend() {
  return (
    <HStack className="duties-legend" flexWrap="wrap" fontSize="12px" spacing={3}>
      <HStack spacing={1}>
        <Image alt="" boxSize="16px" src="admin/images/icons/caregiver.svg" />
        <Text>Mobile</Text>
      </HStack>
      <HStack spacing={1}>
        <Image alt="" boxSize="16px" src="admin/images/icons/call.svg" />
        <Text>IVR</Text>
      </HStack>
      <HStack spacing={1}>
        <Image alt="" boxSize="16px" src="admin/images/icons/operator.svg" />
        <Text>Agency Member</Text>
      </HStack>
    </HStack>
  );
}

function DutySheetEntityPicker(props: {
  imageUrl?: string | null;
  isSelected: boolean;
  label: string;
  onClick: () => void;
}) {
  return (
    <Box
      as="button"
      backgroundColor="transparent"
      border="0"
      className={`unique-entity${props.isSelected ? " selected" : ""}`}
      textAlign="left"
      type="button"
      onClick={props.onClick}
    >
      {props.imageUrl ? (
        <img alt="" className="uprofile-image" src={props.imageUrl} />
      ) : (
        <span className="patients-initials-circle">
          {props.label
            .split(/\s+/)
            .filter(Boolean)
            .slice(0, 2)
            .map((part) => part[0])
            .join("")
            .toUpperCase() || "?"}
        </span>
      )}
      <Text fontWeight={600} ml={2} noOfLines={1}>
        {props.label}
      </Text>
    </Box>
  );
}

function DutySheetCompletionCheckbox(props: {
  id: string;
  isChecked: boolean;
  onChange: (isChecked: boolean) => void;
}) {
  return (
    <div className="round-checkbox">
      <div className="round-checkbox-container">
        <input
          checked={props.isChecked}
          id={props.id}
          type="checkbox"
          onChange={(event) => props.onChange(event.currentTarget.checked)}
        />
        <label className="pointer" htmlFor={props.id} />
      </div>
    </div>
  );
}

function DutySheetNotesButton(props: {
  entry: DutySheetCellEntry;
  onSave: (entry: DutySheetCellEntry, notes: string | null) => void;
}) {
  const [draft, setDraft] = React.useState(props.entry.selectedItem.notes ?? "");

  React.useEffect(() => {
    setDraft(props.entry.selectedItem.notes ?? "");
  }, [props.entry.selectedItem.notes]);

  const saveDraft = useCallback(() => {
    const trimmed = draft.trim();
    const nextNotes = trimmed === "" ? null : trimmed;

    if ((props.entry.selectedItem.notes ?? null) !== nextNotes) {
      props.onSave(props.entry, nextNotes);
    }
  }, [draft, props]);

  return (
    <Popover closeOnBlur placement="bottom-start">
      <PopoverTrigger>
        <Box
          aria-label="Duty notes"
          as="button"
          backgroundColor="transparent"
          border="0"
          display="inline-flex"
          type="button"
        >
          <Image
            alt=""
            boxSize="18px"
            src={
              props.entry.selectedItem.notes
                ? "admin/images/icons/notes-dark.svg"
                : "admin/images/icons/payment-claim.svg"
            }
          />
        </Box>
      </PopoverTrigger>
      <PopoverContent className="popover-duty-sheet-notes" width="280px">
        <PopoverBody>
          <Textarea
            className="duty-sheet-notes-textarea"
            minH="72px"
            placeholder="Notes"
            value={draft}
            onBlur={saveDraft}
            onChange={(event) => setDraft(event.currentTarget.value)}
            onKeyDown={(event) => {
              if (event.key === "Escape") {
                setDraft(props.entry.selectedItem.notes ?? "");
                event.currentTarget.blur();
              }

              if ((event.metaKey || event.ctrlKey) && event.key === "Enter") {
                event.currentTarget.blur();
              }
            }}
          />
        </PopoverBody>
      </PopoverContent>
    </Popover>
  );
}

function DutySheetCellDuty(props: {
  entry: DutySheetCellEntry;
  onAnswer: (entry: DutySheetCellEntry, isCompleted: boolean) => void;
  onSaveNote: (entry: DutySheetCellEntry, notes: string | null) => void;
}) {
  const operatorIcon = getDutySheetOperatorIcon(props.entry.selectedItem.type);
  const checkboxId = dutySheetEntryKey(props.entry, "duty-sheet");

  return (
    <HStack className="caregiver-duty" spacing={2}>
      <HStack className="one-caregiver-day" minW={0} spacing={2}>
        <DutySheetCompletionCheckbox
          id={checkboxId}
          isChecked={props.entry.selectedItem.isCompleted}
          onChange={(isChecked) => props.onAnswer(props.entry, isChecked)}
        />
        {props.entry.caregiverPhotoUrl ? (
          <img alt="" className="table-photo-big" src={props.entry.caregiverPhotoUrl} />
        ) : (
          <span className="patients-initials-circle">
            {props.entry.caregiverName
              .split(/\s+/)
              .filter(Boolean)
              .slice(0, 2)
              .map((part) => part[0])
              .join("")
              .toUpperCase() || "?"}
          </span>
        )}
        <Text fontSize="12px" fontWeight={600} noOfLines={1}>
          {props.entry.caregiverName}
        </Text>
      </HStack>
      <HStack flexShrink={0} spacing={2}>
        {operatorIcon ? <Image alt="" boxSize="18px" src={operatorIcon} /> : null}
        <DutySheetNotesButton entry={props.entry} onSave={props.onSaveNote} />
      </HStack>
    </HStack>
  );
}

function DutySheetGrid(props: {
  row: DutySheetPlanRow;
  selectedCaregiverIds: Set<DutySheetRow["caregiverId"]>;
  weekDays: ReturnType<typeof getDutySheetWeekDays>;
  onAnswer: (entry: DutySheetCellEntry, isCompleted: boolean) => void;
  onSaveNote: (entry: DutySheetCellEntry, notes: string | null) => void;
}) {
  return (
    <TableContainer className="table-container">
      <Table className="patient-duty-sheet-poc-table" size="sm">
        <Thead>
          <Tr>
            <Th>Duty</Th>
            <Th>Code</Th>
            <Th>Section</Th>
            {props.weekDays.map((day) => (
              <Th key={day.key}>
                <div className="day">
                  <span className="date">{day.dayLabel}</span>
                </div>
              </Th>
            ))}
          </Tr>
        </Thead>
        <Tbody>
          {props.row.planOfCare.duties.map((duty) => (
            <Tr key={duty.id}>
              <Td className="task-title-cell">
                <Text as="span" className="task-title">
                  {duty.item.label}
                </Text>
              </Td>
              <Td>{duty.item.code}</Td>
              <Td>{formatPatientProfileValue(getPlanOfCareDutySection(duty))}</Td>
              {props.weekDays.map((day) => {
                const entries = getDutySheetEntriesForDay({
                  day: day.date,
                  duty,
                  row: props.row,
                  selectedCaregiverIds: props.selectedCaregiverIds,
                });

                return (
                  <Td key={day.key}>
                    <VStack alignItems="stretch" className="day-duty-container" spacing={3}>
                      {entries.map((entry) => (
                        <DutySheetCellDuty
                          key={dutySheetEntryKey(entry)}
                          entry={entry}
                          onAnswer={props.onAnswer}
                          onSaveNote={props.onSaveNote}
                        />
                      ))}
                    </VStack>
                  </Td>
                );
              })}
            </Tr>
          ))}
        </Tbody>
      </Table>
    </TableContainer>
  );
}

function PatientProfileDutySheetSection(props: {
  actions: PatientProfileBridgeActions;
  patient?: PatientProfilePatient;
  patientId: PatientProfilePatient["id"];
}) {
  const [startOfWeek, setStartOfWeek] = React.useState(() => getCurrentWeekStart());
  const [showOptionalRows, setShowOptionalRows] = React.useState(false);
  const [selectedPlanOfCareId, setSelectedPlanOfCareId] = React.useState<
    DutySheetPlanRow["planOfCare"]["id"] | null
  >(null);
  const [selectedCaregiverIds, setSelectedCaregiverIds] = React.useState<
    Set<DutySheetRow["caregiverId"]>
  >(new Set());
  const dutySheetsQuery = usePatientDutySheets(props.patientId, startOfWeek);
  const answerDutySheet = useAnswerPatientDutySheet(props.patientId, startOfWeek);
  const submitDutySheetNote = useSubmitPatientDutySheetNote(props.patientId, startOfWeek);
  const rows = useMemo(() => dutySheetsQuery.data?.rows ?? [], [dutySheetsQuery.data?.rows]);
  const selectedRow =
    rows.find((row) => row.planOfCare.id === selectedPlanOfCareId) ?? rows[0] ?? null;
  const caregiverFilters = useMemo(
    () => (selectedRow === null ? [] : getDutySheetCaregiverFilters(selectedRow)),
    [selectedRow]
  );
  const weekDays = useMemo(() => getDutySheetWeekDays(startOfWeek), [startOfWeek]);

  React.useEffect(() => {
    if (rows.length === 0) {
      setSelectedPlanOfCareId(null);
      return;
    }

    if (!rows.some((row) => row.planOfCare.id === selectedPlanOfCareId)) {
      setSelectedPlanOfCareId(rows[0].planOfCare.id);
    }
  }, [rows, selectedPlanOfCareId]);

  React.useEffect(() => {
    setSelectedCaregiverIds(new Set(caregiverFilters.map((caregiver) => caregiver.caregiverId)));
  }, [caregiverFilters]);

  const toggleCaregiver = useCallback((caregiverId: DutySheetRow["caregiverId"]) => {
    setSelectedCaregiverIds((current) => {
      const next = new Set(current);

      if (next.has(caregiverId)) {
        next.delete(caregiverId);
      } else {
        next.add(caregiverId);
      }

      return next;
    });
  }, []);

  const answerDuty = useCallback(
    (entry: DutySheetCellEntry, isCompleted: boolean) => {
      answerDutySheet.mutate({
        completed: isCompleted,
        planOfCareItemId: entry.selectedItem.planOfCareItemId,
        occurrenceIndex: entry.selectedItem.occurrenceIndex,
        visitInstanceId: entry.visitInstanceId,
      });
    },
    [answerDutySheet]
  );

  const saveNote = useCallback(
    (entry: DutySheetCellEntry, notes: string | null) => {
      submitDutySheetNote.mutate({
        notes,
        planOfCareItemId: entry.selectedItem.planOfCareItemId,
        occurrenceIndex: entry.selectedItem.occurrenceIndex,
        visitInstanceId: entry.visitInstanceId,
      });
    },
    [submitDutySheetNote]
  );

  const exportDutySheetData = useCallback(() => {
    if (selectedRow === null || props.patient === undefined) {
      return;
    }

    props.actions.openPatientDutySheetExport({
      data: {
        fromDate: startOfWeek,
        planOfCareId: selectedRow.planOfCare.id,
      },
      onDone: () => void dutySheetsQuery.refetch(),
      patient: props.patient,
    });
  }, [dutySheetsQuery, props.actions, props.patient, selectedRow, startOfWeek]);

  return (
    <EntityProfileQuerySection
      errorLabel="Could not load duty sheets."
      isError={dutySheetsQuery.isError}
      isLoading={dutySheetsQuery.isLoading}
      loadingLabel="Loading duty sheets"
      onRetry={() => void dutySheetsQuery.refetch()}
    >
      <VStack
        alignItems="stretch"
        className="patient-duty-sheet duty-sheets"
        spacing={5}
        sx={{
          ".filter-entities-container": {
            marginTop: "12px",
          },
          ".duty-sheet-selector-title": {
            color: "#283847",
            fontSize: "20px",
            fontWeight: 400,
            marginBottom: "10px",
          },
          ".unique-entity": {
            border: "1px solid #e3e5e7",
            borderRadius: "5px",
            boxShadow: "0 1px 4px rgba(0, 0, 0, 0.14)",
            flex: "0 0 auto",
            minHeight: "42px",
            minWidth: "150px",
            padding: "6px 10px",
            width: "auto",
          },
          ".unique-entity:hover": {
            boxShadow: "0 2px 8px rgba(0, 0, 0, 0.18)",
            transform: "none",
          },
          ".unique-entity.selected": {
            backgroundColor: "#4f5df7",
            color: "#fff",
          },
          ".unique-entity .patients-initials-circle": {
            backgroundColor: "#223046",
            color: "#fff",
            fontSize: "13px",
            height: "28px",
            marginLeft: 0,
            width: "28px",
          },
          ".unique-entity .uprofile-image": {
            height: "28px",
            width: "28px",
          },
        }}
      >
        <HStack flexWrap="wrap" justifyContent="space-between" spacing={3}>
          <HStack flexWrap="wrap" spacing={3}>
            <Text className="week-title">
              {formatCertificationPeriodDate(startOfWeek)} -{" "}
              {formatCertificationPeriodDate(startOfWeek.plusDays(6))}
            </Text>
            <EntityModalButtonGroup>
              <EntityModalButton
                aria-label="Previous week"
                iconClassName="fa fa-chevron-left"
                kind="white"
                size="sm"
                title="Previous week"
                onClick={() => setStartOfWeek((current) => current.minusWeeks(1))}
              />
              <EntityModalButton
                aria-label="Next week"
                iconClassName="fa fa-chevron-right"
                kind="white"
                size="sm"
                title="Next week"
                onClick={() => setStartOfWeek((current) => current.plusWeeks(1))}
              />
            </EntityModalButtonGroup>
          </HStack>
          <HStack flexWrap="wrap" justifyContent="flex-end" spacing={3}>
            <DutySheetLegend />
            <EntityModalButton
              disabled={selectedRow === null}
              kind="blue"
              onClick={() => setShowOptionalRows((current) => !current)}
            >
              {showOptionalRows ? "Hide optional rows" : "Show optional rows"}
            </EntityModalButton>
            <EntityModalButton
              disabled={selectedRow === null || props.patient === undefined}
              kind="blue"
              variant="outline"
              onClick={exportDutySheetData}
            >
              Export Data
            </EntityModalButton>
          </HStack>
        </HStack>

        {dutySheetsQuery.data?.hasPlanOfCare === false ? (
          <EntityProfileEmptyState>
            Patient does not have any plans of care.
          </EntityProfileEmptyState>
        ) : rows.length === 0 || selectedRow === null ? (
          <EntityProfileEmptyState>
            Patient does not have any duty tasks for the current week.
          </EntityProfileEmptyState>
        ) : (
          <>
            <VStack alignItems="stretch" className="filter-entities-container" spacing={3}>
              {caregiverFilters.length > 0 ? (
                <Box>
                  <Text className="duty-sheet-selector-title">Selected Caregiver</Text>
                  <HStack alignItems="center" className="entities-container" flexWrap="wrap">
                    {caregiverFilters.map((caregiver) => (
                      <DutySheetEntityPicker
                        key={caregiver.caregiverId}
                        imageUrl={caregiver.photoUrl}
                        isSelected={selectedCaregiverIds.has(caregiver.caregiverId)}
                        label={caregiver.displayName}
                        onClick={() => toggleCaregiver(caregiver.caregiverId)}
                      />
                    ))}
                  </HStack>
                </Box>
              ) : null}
              {rows.length > 0 ? (
                <Box>
                  <HStack alignItems="flex-start" spacing={1}>
                    <Text className="duty-sheet-selector-title">
                      {rows.length === 1 ? "POC" : "Select Relevant POC"}
                    </Text>
                    {rows.length > 1 ? (
                      <Box as="span" className="help-icon" marginTop="4px">
                        <i
                          className="fa fa-question-circle"
                          title="This week has different visits that require different POCs"
                        />
                      </Box>
                    ) : null}
                  </HStack>
                  <HStack alignItems="center" className="entities-container" flexWrap="wrap">
                    {rows.map((row) => (
                      <DutySheetEntityPicker
                        key={row.planOfCare.id}
                        isSelected={row.planOfCare.id === selectedRow.planOfCare.id}
                        label={`Plan ${formatPlanOfCareDate(
                          row.planOfCare.startDate
                        )} - ${formatPlanOfCareDate(row.planOfCare.endDate)}`}
                        onClick={() => setSelectedPlanOfCareId(row.planOfCare.id)}
                      />
                    ))}
                  </HStack>
                </Box>
              ) : null}
            </VStack>
            <DutySheetGrid
              row={selectedRow}
              selectedCaregiverIds={selectedCaregiverIds}
              weekDays={weekDays}
              onAnswer={answerDuty}
              onSaveNote={saveNote}
            />
          </>
        )}
      </VStack>
    </EntityProfileQuerySection>
  );
}

type PatientPhysicianSortKey = "id" | "name" | "npi" | "phone" | "fax" | "primary";

function getPatientPhysicianName(physician: PatientPhysicianAssociation) {
  return formatPatientProfileValue(physician.displayName);
}

function formatPatientPhysicianPhone(phone: string | null | undefined) {
  if (phone === null || phone === undefined || phone.trim() === "") {
    return formatPatientProfileValue(phone);
  }

  const digits = phone.replace(/\D/g, "");

  if (digits.length === 10) {
    return `+1 (${digits.slice(0, 3)}) ${digits.slice(3, 6)}-${digits.slice(6)}`;
  }

  if (digits.length === 11 && digits.startsWith("1")) {
    return `+1 (${digits.slice(1, 4)}) ${digits.slice(4, 7)}-${digits.slice(7)}`;
  }

  return phone.trim();
}

function PatientProfilePhysiciansSection(props: {
  actions: PatientProfileBridgeActions;
  patient?: PatientProfilePatient;
  patientId: PatientProfilePatient["id"];
}) {
  const physiciansQuery = usePatientPhysicianAssociations(props.patientId);
  const physicians = physiciansQuery.data?.physicians ?? [];
  const refresh = useCallback(() => void physiciansQuery.refetch(), [physiciansQuery]);
  const columns = useMemo<
    Array<EntityProfileDataTableColumn<PatientPhysicianAssociation, PatientPhysicianSortKey>>
  >(
    () => [
      {
        header: "ID",
        key: "id",
        render: (physician) => physician.id,
        sortKey: "id",
        sortValue: (physician) => physician.id,
      },
      {
        header: "Name",
        key: "name",
        render: getPatientPhysicianName,
        sortKey: "name",
        sortValue: getPatientPhysicianName,
      },
      {
        header: "Address",
        key: "address",
        render: (physician) => formatPatientProfileValue(physician.address),
      },
      {
        header: "NPI",
        key: "npi",
        render: (physician) => formatPatientProfileValue(physician.npi),
        sortKey: "npi",
        sortValue: (physician) => physician.npi ?? "",
      },
      {
        header: "Phone",
        key: "phone",
        render: (physician) => formatPatientPhysicianPhone(physician.phone),
        sortKey: "phone",
        sortValue: (physician) => physician.phone ?? "",
      },
      {
        header: "Fax",
        key: "fax",
        render: (physician) => formatPatientPhysicianPhone(physician.fax),
        sortKey: "fax",
        sortValue: (physician) => physician.fax ?? "",
      },
      {
        cellProps: { textAlign: "center" },
        header: "Is Primary",
        key: "isPrimary",
        render: (physician) => (
          <Checkbox
            aria-label={`Set ${getPatientPhysicianName(physician)} as primary physician`}
            colorScheme="blue"
            isChecked={physician.isPrimary === true}
            isDisabled={props.patient === undefined}
            justifyContent="center"
            onChange={(event) => {
              if (props.patient === undefined) {
                return;
              }

              props.actions.updatePatientPhysicianPrimary({
                data: { ...physician, isPrimary: event.currentTarget.checked },
                onDone: refresh,
                patient: props.patient,
              });
            }}
          />
        ),
        sortKey: "primary",
        sortValue: (physician) => (physician.isPrimary === true ? 1 : 0),
      },
      {
        cellProps: { textAlign: "left" },
        header: "Actions",
        key: "actions",
        render: (physician) => (
          <EntityModalButton
            aria-label={`Delete ${getPatientPhysicianName(physician)} physician`}
            disabled={props.patient === undefined}
            iconClassName="fa fa-trash"
            kind="red"
            title="Delete physician"
            onClick={() => {
              if (props.patient === undefined) {
                return;
              }

              props.actions.deletePatientPhysician({
                data: physician,
                onDone: refresh,
                patient: props.patient,
              });
            }}
          />
        ),
      },
    ],
    [props.actions, props.patient, refresh]
  );

  const actions = (
    <SectionActions>
      <EntityModalButton
        disabled={props.patient === undefined}
        kind="blue"
        size="sm"
        onClick={() => {
          if (props.patient === undefined) {
            return;
          }

          props.actions.openPatientPhysician({
            data: { patientPhysicians: physicians },
            onDone: refresh,
            patient: props.patient,
          });
        }}
      >
        Add Physician
      </EntityModalButton>
    </SectionActions>
  );

  return (
    <EntityProfileQuerySection
      errorLabel="Could not load physicians."
      isError={physiciansQuery.isError}
      isLoading={physiciansQuery.isLoading}
      loadingLabel="Loading physicians"
      onRetry={() => void physiciansQuery.refetch()}
    >
      <VStack alignItems="stretch" spacing={3}>
        <HStack justifyContent="flex-end">{actions}</HStack>
        {physicians.length === 0 ? (
          <EntityProfileEmptyState>
            No Patient Physicians found, Please add one by clicking on the Add Physician button
          </EntityProfileEmptyState>
        ) : (
          <EntityProfileDataTable
            columns={columns}
            defaultPageSize={10}
            defaultSort={{ direction: "desc", key: "id" }}
            getRowKey={(physician) => physician.id}
            rows={physicians}
          />
        )}
      </VStack>
    </EntityProfileQuerySection>
  );
}

type MedicationProfileSortKey = "name" | "dose" | "route" | "frequency" | "from" | "until";

function getMedicationName(medication: PatientMedicationProfileRow) {
  return formatPatientProfileValue(medication.medicationName ?? medication.name);
}

function formatMedicationProfileDate(value: string | null | undefined) {
  if (value === null || value === undefined || value.trim() === "") {
    return formatPatientProfileValue(value);
  }

  try {
    return dateFormatter.toDate(LocalDate.parse(value.slice(0, 10)));
  } catch {
    return formatPatientProfileValue(value);
  }
}

function PatientProfileMedicationProfileSection(props: {
  actions: PatientProfileBridgeActions;
  patient?: PatientProfilePatient;
  patientId: PatientProfilePatient["id"];
}) {
  const medicationsQuery = usePatientMedicationProfile(props.patientId);
  const medications = medicationsQuery.data?.medications ?? [];
  const [isAddMedicationOpen, setIsAddMedicationOpen] = useState(false);
  const refresh = useCallback(() => void medicationsQuery.refetch(), [medicationsQuery]);
  const openLegacyMedicationProfile = useCallback(
    (action: string, medication?: PatientMedicationProfileRow) => {
      if (props.patient === undefined) {
        return;
      }

      props.actions.openPatientMedicationProfileLegacy({
        data: { action, medication, sectionId: "medication-profile" },
        onDone: refresh,
        patient: props.patient,
      });
    },
    [props.actions, props.patient, refresh]
  );
  const columns = useMemo<
    Array<EntityProfileDataTableColumn<PatientMedicationProfileRow, MedicationProfileSortKey>>
  >(
    () => [
      {
        header: "Medication Name",
        key: "name",
        render: (medication) => (
          <VStack alignItems="flex-start" spacing={1}>
            <Text fontWeight={600}>{getMedicationName(medication)}</Text>
            {medication.notes ? (
              <Text color={entityProfileModalColors.secondaryText} fontSize="12px">
                Notes: {medication.notes}
              </Text>
            ) : null}
          </VStack>
        ),
        sortKey: "name",
        sortValue: getMedicationName,
      },
      {
        header: "Dose",
        key: "dose",
        render: (medication) => formatPatientProfileValue(medication.dose),
        sortKey: "dose",
        sortValue: (medication) => medication.dose ?? "",
      },
      {
        header: "Route",
        key: "route",
        render: (medication) => formatPatientProfileValue(medication.route),
        sortKey: "route",
        sortValue: (medication) => medication.route ?? "",
      },
      {
        header: "Frequency",
        key: "frequency",
        render: (medication) => formatPatientProfileValue(medication.frequency),
        sortKey: "frequency",
        sortValue: (medication) => medication.frequency ?? "",
      },
      {
        header: "From",
        key: "from",
        render: (medication) => formatMedicationProfileDate(medication.from),
        sortKey: "from",
        sortValue: (medication) => medication.from ?? "",
      },
      {
        header: "Until",
        key: "until",
        render: (medication) => formatMedicationProfileDate(medication.until),
        sortKey: "until",
        sortValue: (medication) => medication.until ?? "",
      },
      {
        cellProps: { textAlign: "center" },
        header: "Actions",
        key: "actions",
        render: (medication) => (
          <HStack justifyContent="center" spacing={2}>
            <EntityModalButton
              isIconOnly
              aria-label={`Edit ${getMedicationName(medication)} medication`}
              disabled={props.patient === undefined}
              iconClassName="fa fa-pencil-square-o"
              kind="white"
              title="Edit medication"
              onClick={() => openLegacyMedicationProfile("edit", medication)}
            />
            <EntityModalButton
              isIconOnly
              aria-label={`Delete ${getMedicationName(medication)} medication`}
              disabled={props.patient === undefined}
              iconClassName="fa fa-trash"
              kind="red"
              title="Delete medication"
              onClick={() => {
                if (props.patient === undefined) {
                  return;
                }

                props.actions.deletePatientMedicationProfile({
                  data: medication,
                  onDone: refresh,
                  patient: props.patient,
                });
              }}
            />
          </HStack>
        ),
      },
    ],
    [openLegacyMedicationProfile, props.actions, props.patient, refresh]
  );

  return (
    <EntityProfileQuerySection
      errorLabel="Could not load medications."
      isError={medicationsQuery.isError}
      isLoading={medicationsQuery.isLoading}
      loadingLabel="Loading medications"
      onRetry={() => void medicationsQuery.refetch()}
    >
      <VStack alignItems="stretch" spacing={3}>
        <HStack justifyContent="flex-end" spacing={2}>
          <EntityModalButton
            iconClassName="fa fa-plus"
            kind="blue"
            size="sm"
            onClick={() => setIsAddMedicationOpen(true)}
          >
            Add Medication
          </EntityModalButton>
        </HStack>
        {medications.length === 0 ? (
          <EntityProfileEmptyState>
            <VStack spacing={1}>
              <Text>No medications added yet.</Text>
              <Text>Click “Add Medication“ to get started.</Text>
            </VStack>
          </EntityProfileEmptyState>
        ) : (
          <EntityProfileDataTable
            columns={columns}
            defaultPageSize={10}
            getRowKey={(medication) => medication.id}
            rows={medications}
          />
        )}
        <AddEditMedicationModal
          isOpen={isAddMedicationOpen}
          patientId={props.patientId}
          onClose={() => setIsAddMedicationOpen(false)}
          onSuccess={refresh}
        />
      </VStack>
    </EntityProfileQuerySection>
  );
}

function PatientProfileTasksSection(props: { patientId: PatientProfilePatient["id"] }) {
  const taskHistoryQuery = usePatientTaskHistory(props.patientId);
  const records = taskHistoryQuery.data?.summary ?? [];

  return (
    <EntityProfileQuerySection
      errorLabel="Could not load task activity."
      isError={taskHistoryQuery.isError}
      isLoading={taskHistoryQuery.isLoading}
      loadingLabel="Loading task activity"
      onRetry={() => void taskHistoryQuery.refetch()}
    >
      {records.length === 0 ? (
        <EntityProfileEmptyState>No task activity.</EntityProfileEmptyState>
      ) : (
        <VStack alignItems="stretch" maxH="500px" overflowY="auto" spacing={0}>
          {records.map((record, index) => (
            <Box
              key={`${record.updatedAt.toString()}-${index}`}
              borderBottom={entityProfileModalBorders.default}
              py={3}
            >
              <HStack justifyContent="space-between" mb={1}>
                <Text color={entityProfileModalColors.text} fontSize="14px" fontWeight={700}>
                  {record.actor?.type === "SYSTEM" ? "System" : record.actor?.name ?? "Unknown"}
                </Text>
                <Text color={entityProfileModalColors.muted} fontSize="13px">
                  {formatPatientProfileValue(record.updatedAt)}
                </Text>
              </HStack>
              <Text color={entityProfileModalColors.secondaryText} fontSize="13px">
                {record.summary}
              </Text>
            </Box>
          ))}
        </VStack>
      )}
    </EntityProfileQuerySection>
  );
}

type PatientDocumentStatus = typeof PATIENT_DOCUMENT_STATUS_OPTIONS[number]["value"];
type PatientDocumentSortKey =
  | "type"
  | "origin"
  | "scheduledDate"
  | "submittedBy"
  | "submittedAt"
  | "signedAt"
  | "status"
  | "certificationPeriod";
type PatientDocumentFilterKey =
  | "certificationPeriod"
  | "scheduledDate"
  | "status"
  | "submittedByName"
  | "title";

const patientDocumentFilterSelectButtonProps = {
  _hover: { bg: "#fff", borderColor: "#adadad" },
  bg: "#fff",
  borderColor: "#ccc",
  borderRadius: "2px",
  color: "#333",
  fontSize: "15px",
  fontWeight: 400,
  h: "34px",
  minW: "100%",
  px: "12px",
};

function getPatientDocumentDateValue(value: unknown) {
  if (value === null || value === undefined || value === "") {
    return "";
  }

  return String(value);
}

function getPatientDocumentFilterValue(
  document: PatientDocumentRow,
  key: PatientDocumentFilterKey
) {
  switch (key) {
    case "certificationPeriod":
      return getPatientDocumentCertificationPeriod(document);
    case "scheduledDate":
      return getPatientDocumentScheduledDate(document);
    case "status":
      return getPatientDocumentStatus(document);
    case "submittedByName":
      return getPatientDocumentSubmittedBy(document);
    case "title":
      return document.title;
  }
}

function getPatientDocumentFilterLabel(key: PatientDocumentFilterKey, value: string) {
  switch (key) {
    case "scheduledDate":
      return getPatientDocumentDate(value);
    case "status":
      return getPatientDocumentStatusLabel(value as Exclude<PatientDocumentStatus, "all">);
    default:
      return value;
  }
}

function getPatientDocumentFilterOptions(
  documents: PatientDocumentRow[],
  key: PatientDocumentFilterKey
) {
  return Array.from(
    new Set(
      documents
        .map((document) =>
          getPatientDocumentDateValue(getPatientDocumentFilterValue(document, key))
        )
        .filter((value) => value !== "")
    )
  )
    .map((value) => ({ label: getPatientDocumentFilterLabel(key, value), value }))
    .sort((left, right) => left.label.localeCompare(right.label, undefined, { numeric: true }));
}

function getPatientDocumentSelectedFilterLabel(
  values: string[],
  options: Array<{ label: string; value: string }>
) {
  if (values.length === 1) {
    return options.find((option) => option.value === values[0])?.label ?? values[0];
  }

  return `${values.length} selected`;
}

function getPatientDocumentDate(value: unknown) {
  const normalizedValue = getPatientDocumentDateValue(value);

  if (normalizedValue === "") {
    return "";
  }

  try {
    return dateFormatter.toDate(LocalDate.parse(normalizedValue.slice(0, 10)));
  } catch {
    return formatPatientProfileValue(normalizedValue);
  }
}

function parsePatientDocumentLocalDate(value: unknown) {
  const normalizedValue = getPatientDocumentDateValue(value);

  if (normalizedValue === "") {
    return null;
  }

  try {
    return LocalDate.parse(normalizedValue.slice(0, 10));
  } catch {
    return null;
  }
}

function getPatientDocumentStatus(
  document: PatientDocumentRow
): Exclude<PatientDocumentStatus, "all"> {
  if ("isOpenForResubmission" in document && document.isOpenForResubmission) {
    return "OPEN_FOR_RESUBMISSION";
  }

  if ("hasUnresolvedChangeRequest" in document && document.hasUnresolvedChangeRequest) {
    return "CHANGE_REQUESTED";
  }

  if ("approvedDate" in document && document.approvedDate) {
    return "APPROVED";
  }

  if ("physicianSignDate" in document && document.physicianSignDate) {
    return "SIGNED";
  }

  if ("sentToPhysicianDate" in document && document.sentToPhysicianDate) {
    return "SENT_TO_PHYSICIAN";
  }

  if (
    "wasSentViaEmail" in document &&
    "wasSentViaFax" in document &&
    (document.wasSentViaEmail || document.wasSentViaFax)
  ) {
    return "SENT";
  }

  if (document.submittedAt !== null) {
    return "COMPLETED";
  }

  if ("answers" in document && document.answers.length > 0) {
    return "IN_PROGRESS";
  }

  return "MISSING";
}

function getPatientDocumentStatusLabel(status: Exclude<PatientDocumentStatus, "all">) {
  return PATIENT_DOCUMENT_STATUS_OPTIONS.find((option) => option.value === status)?.label ?? status;
}

function getPatientDocumentStatusColor(status: Exclude<PatientDocumentStatus, "all">) {
  switch (status) {
    case "APPROVED":
    case "SENT_TO_PHYSICIAN":
      return "blue";
    case "CHANGE_REQUESTED":
    case "IN_PROGRESS":
    case "OPEN_FOR_RESUBMISSION":
      return "orange";
    case "COMPLETED":
    case "SENT":
    case "SIGNED":
      return "green";
    case "MISSING":
      return "red";
  }
}

function getPatientDocumentOrigin(document: PatientDocumentRow) {
  switch (document.__typename) {
    case "PatientExpectedDocumentObjectAgencyMember":
      return `Coordinator${document.submittedByName ? ` (${document.submittedByName})` : ""}`;
    case "PatientExpectedDocumentObjectVisit":
    case "PatientExpectedScannedDocumentObjectVisit":
      return `Visit #${document.scheduledVisitId}`;
    case "PatientExpectedDocumentObjectTask":
    case "PatientExpectedScannedDocumentObjectTask":
      return "Task";
    default:
      return "";
  }
}

function getPatientDocumentScheduledDate(document: PatientDocumentRow) {
  return "scheduledDate" in document ? document.scheduledDate : null;
}

function getPatientDocumentSubmittedBy(document: PatientDocumentRow) {
  return "submittedByName" in document ? document.submittedByName : null;
}

function getPatientDocumentCertificationPeriod(document: PatientDocumentRow) {
  return "certificationPeriod" in document ? document.certificationPeriod : undefined;
}

function getPatientDocumentIsDeletedTask(document: PatientDocumentRow) {
  return "taskRemoved" in document && document.taskRemoved;
}

function isPatientVisitDocument(document: PatientDocumentRow): document is PatientVisitDocumentRow {
  return (
    document.__typename === "PatientExpectedDocumentObjectVisit" ||
    document.__typename === "PatientExpectedScannedDocumentObjectVisit"
  );
}

function PatientDocumentActionMenu(props: {
  actions: PatientProfileBridgeActions;
  document: PatientDocumentRow;
}) {
  const pdfUrl = "pdfUrl" in props.document ? props.document.pdfUrl : null;
  const canViewPdf = pdfUrl !== null;
  const visitDocument = isPatientVisitDocument(props.document) ? props.document : null;
  const canOpenVisit = visitDocument !== null;

  return (
    <Menu placement="bottom-end">
      <MenuButton
        _active={{ bg: "#00a6bd" }}
        _hover={{ bg: "#00a6bd" }}
        aria-label="Document actions"
        as={Button}
        bg="#00bcd4"
        borderRadius="full"
        color="#fff"
        fontSize="20px"
        h="32px"
        lineHeight="1"
        minW="32px"
        p={0}
        onClick={(event) => event.stopPropagation()}
      >
        ⋮
      </MenuButton>
      <MenuList fontSize="14px" minW="170px" py={0} onClick={(event) => event.stopPropagation()}>
        {canViewPdf ? (
          <MenuItem as="a" href={pdfUrl ?? undefined} target="_blank">
            View PDF
          </MenuItem>
        ) : null}
        {canOpenVisit ? (
          <MenuItem
            onClick={() => props.actions.navigateToVisitInstance(visitDocument.scheduledVisitId)}
          >
            Go to visit
          </MenuItem>
        ) : null}
        {!canViewPdf && !canOpenVisit ? <MenuItem isDisabled>No actions</MenuItem> : null}
      </MenuList>
    </Menu>
  );
}

function PatientProfileDocumentsSection(props: {
  actions: PatientProfileBridgeActions;
  patient?: PatientProfilePatient;
  patientId: PatientProfilePatient["id"];
}) {
  const documentsQuery = usePatientDocuments(props.patientId);
  const allDocuments = useMemo(
    () => [
      ...(documentsQuery.data?.documents ?? []),
      ...(documentsQuery.data?.scannedDocuments ?? []),
    ],
    [documentsQuery.data?.documents, documentsQuery.data?.scannedDocuments]
  );
  const [documentTitleFilter, setDocumentTitleFilter] = React.useState<string[]>([]);
  const [visitDateFilter, setVisitDateFilter] = React.useState<string[]>([]);
  const [submittedByFilter, setSubmittedByFilter] = React.useState<string[]>([]);
  const [statusFilter, setStatusFilter] = React.useState<string[]>([]);
  const [certificationPeriodFilter, setCertificationPeriodFilter] = React.useState<string[]>([]);
  const [showDeletedTaskDocuments, setShowDeletedTaskDocuments] = React.useState(false);
  const [scheduledDateRange, setScheduledDateRange] = React.useState<
    [LocalDate | null, LocalDate | null]
  >([null, null]);
  const documentTitleOptions = useMemo(
    () => getPatientDocumentFilterOptions(allDocuments, "title"),
    [allDocuments]
  );
  const visitDateOptions = useMemo(
    () => getPatientDocumentFilterOptions(allDocuments, "scheduledDate"),
    [allDocuments]
  );
  const submittedByOptions = useMemo(
    () => getPatientDocumentFilterOptions(allDocuments, "submittedByName"),
    [allDocuments]
  );
  const statusOptions = useMemo(
    () => getPatientDocumentFilterOptions(allDocuments, "status"),
    [allDocuments]
  );
  const certificationPeriodOptions = useMemo(
    () => getPatientDocumentFilterOptions(allDocuments, "certificationPeriod"),
    [allDocuments]
  );
  const filteredDocuments = useMemo(
    () =>
      allDocuments.filter((document) => {
        if (!showDeletedTaskDocuments && getPatientDocumentIsDeletedTask(document)) {
          return false;
        }

        if (documentTitleFilter.length > 0 && !documentTitleFilter.includes(document.title)) {
          return false;
        }

        const documentStatus = getPatientDocumentStatus(document);

        if (statusFilter.length > 0 && !statusFilter.includes(documentStatus)) {
          return false;
        }

        const submittedBy = getPatientDocumentSubmittedBy(document);

        if (
          submittedByFilter.length > 0 &&
          (submittedBy === null || !submittedByFilter.includes(submittedBy))
        ) {
          return false;
        }

        const certificationPeriod = getPatientDocumentCertificationPeriod(document);

        if (
          certificationPeriodFilter.length > 0 &&
          (certificationPeriod === undefined ||
            certificationPeriod === null ||
            !certificationPeriodFilter.includes(certificationPeriod))
        ) {
          return false;
        }

        const scheduledDate = getPatientDocumentScheduledDate(document);
        const scheduledDateValue = getPatientDocumentDateValue(scheduledDate);

        if (
          visitDateFilter.length > 0 &&
          (scheduledDateValue === "" || !visitDateFilter.includes(scheduledDateValue))
        ) {
          return false;
        }

        if (
          scheduledDate === null &&
          (scheduledDateRange[0] !== null || scheduledDateRange[1] !== null)
        ) {
          return false;
        }

        if (scheduledDate !== null && scheduledDateRange[0] !== null) {
          const parsedDate = parsePatientDocumentLocalDate(scheduledDate);

          if (parsedDate === null || parsedDate.isBefore(scheduledDateRange[0])) {
            return false;
          }
        }

        if (scheduledDate !== null && scheduledDateRange[1] !== null) {
          const parsedDate = parsePatientDocumentLocalDate(scheduledDate);

          if (parsedDate === null || parsedDate.isAfter(scheduledDateRange[1])) {
            return false;
          }
        }

        return true;
      }),
    [
      allDocuments,
      certificationPeriodFilter,
      documentTitleFilter,
      scheduledDateRange,
      showDeletedTaskDocuments,
      statusFilter,
      submittedByFilter,
      visitDateFilter,
    ]
  );
  const columns = useMemo<
    Array<EntityProfileDataTableColumn<PatientDocumentRow, PatientDocumentSortKey>>
  >(
    () => [
      {
        header: "Document Type",
        key: "type",
        render: (document) => (
          <HStack spacing={2}>
            <Text>{document.title}</Text>
            {"copiedFrom" in document && document.copiedFrom !== null ? (
              <Badge colorScheme="blue">Copied</Badge>
            ) : null}
          </HStack>
        ),
        sortKey: "type",
        sortValue: (document) => document.title,
      },
      {
        header: "Origin",
        key: "origin",
        render: (document) => {
          const origin = getPatientDocumentOrigin(document);

          if (isPatientVisitDocument(document)) {
            return (
              <EntityModalButton
                kind="blue"
                style={{ color: "#337ab7", fontSize: "14px", textDecoration: "underline" }}
                variant="link"
                onClick={() => props.actions.navigateToVisitInstance(document.scheduledVisitId)}
              >
                {origin} ({getPatientDocumentDate(document.scheduledDate)})
              </EntityModalButton>
            );
          }

          return origin;
        },
        sortKey: "origin",
        sortValue: getPatientDocumentOrigin,
      },
      {
        header: "Scheduled for visit",
        key: "scheduledDate",
        render: (document) => getPatientDocumentDate(getPatientDocumentScheduledDate(document)),
        sortKey: "scheduledDate",
        sortValue: (document) =>
          getPatientDocumentDateValue(getPatientDocumentScheduledDate(document)),
      },
      {
        header: "Submitted by",
        key: "submittedBy",
        render: (document) => formatPatientProfileValue(getPatientDocumentSubmittedBy(document)),
        sortKey: "submittedBy",
        sortValue: (document) => getPatientDocumentSubmittedBy(document) ?? "",
      },
      {
        header: "Submitted at",
        key: "submittedAt",
        render: (document) => (
          <VStack alignItems="flex-start" spacing={1}>
            <Text>{getPatientDocumentDate(document.submittedAt)}</Text>
            {"pdfUrl" in document && document.submittedAt !== null && document.pdfUrl === null ? (
              <Badge colorScheme="blue">No file attached</Badge>
            ) : null}
          </VStack>
        ),
        sortKey: "submittedAt",
        sortValue: (document) => getPatientDocumentDateValue(document.submittedAt),
      },
      {
        header: "Signed at",
        key: "signedAt",
        render: (document) =>
          "physicianSignDate" in document ? getPatientDocumentDate(document.physicianSignDate) : "",
        sortKey: "signedAt",
        sortValue: (document) =>
          "physicianSignDate" in document
            ? getPatientDocumentDateValue(document.physicianSignDate)
            : "",
      },
      {
        header: "Status",
        key: "status",
        render: (document) => {
          const status = getPatientDocumentStatus(document);

          return (
            <Badge colorScheme={getPatientDocumentStatusColor(status)} textTransform="none">
              {getPatientDocumentStatusLabel(status)}
            </Badge>
          );
        },
        sortKey: "status",
        sortValue: getPatientDocumentStatus,
      },
      {
        header: "Certification Period",
        key: "certificationPeriod",
        render: (document) =>
          formatPatientProfileValue(getPatientDocumentCertificationPeriod(document)),
        sortKey: "certificationPeriod",
        sortValue: (document) => getPatientDocumentCertificationPeriod(document) ?? "",
      },
      {
        cellAlign: "center",
        header: "Action",
        key: "action",
        render: (document) => (
          <PatientDocumentActionMenu actions={props.actions} document={document} />
        ),
      },
    ],
    [props.actions]
  );

  return (
    <EntityProfileQuerySection
      errorLabel="Could not load documents."
      isError={documentsQuery.isError}
      isLoading={documentsQuery.isLoading}
      loadingLabel="Loading documents"
      onRetry={() => void documentsQuery.refetch()}
    >
      <VStack alignItems="stretch" spacing={4}>
        <PatientTaskDocumentChangeRequestsTable
          patientId={props.patientId}
          onResolve={() => void documentsQuery.refetch()}
        />

        <PatientTaskDocumentRejectionsTable
          patientId={props.patientId}
          onUpdate={() => void documentsQuery.refetch()}
        />

        <HStack justifyContent="flex-end">
          <EntityModalButton
            disabled={props.patient === undefined}
            kind="blue"
            size="sm"
            onClick={() => {
              if (props.patient === undefined) {
                return;
              }

              props.actions.openPatientDocument({
                onDone: () => void documentsQuery.refetch(),
                patient: props.patient,
              });
            }}
          >
            Add document
          </EntityModalButton>
        </HStack>

        <HStack alignItems="center" spacing={2}>
          <Checkbox
            isChecked={showDeletedTaskDocuments}
            onChange={(event) => setShowDeletedTaskDocuments(event.target.checked)}
          >
            Also show documents from deleted tasks
          </Checkbox>
        </HStack>

        <EntityProfileFilterBar>
          <EntityProfileFilterBarItem flex="0 0 260px" label="Dates" maxW="260px">
            <InputGroup>
              <RangeDatePicker
                dateFormat="MM/dd/yyyy"
                endDate={scheduledDateRange[1]}
                inputProps={{ h: "34px", fontSize: "14px", width: "full" }}
                selected={scheduledDateRange[1] ?? scheduledDateRange[0]}
                startDate={scheduledDateRange[0]}
                onChange={(date) => {
                  if (date === null) {
                    setScheduledDateRange([null, null]);
                    return;
                  }

                  setScheduledDateRange(date);
                }}
              />
              <InputRightElement h="34px">
                <CalendarIcon color={entityProfileModalColors.muted} />
              </InputRightElement>
            </InputGroup>
          </EntityProfileFilterBarItem>
          <EntityProfileFilterBarItem flex="0 0 220px" label="Document name" maxW="220px">
            <SharedSelect
              buttonProps={patientDocumentFilterSelectButtonProps}
              label="All documents"
              multiple={true}
              options={documentTitleOptions}
              searchable={documentTitleOptions.length > 5}
              selectedLabel={(value) =>
                getPatientDocumentSelectedFilterLabel(value, documentTitleOptions)
              }
              value={documentTitleFilter.length > 0 ? documentTitleFilter : null}
              onChange={(value) => setDocumentTitleFilter(value ?? [])}
            />
          </EntityProfileFilterBarItem>
          <EntityProfileFilterBarItem flex="0 0 170px" label="Visit date" maxW="170px">
            <SharedSelect
              buttonProps={patientDocumentFilterSelectButtonProps}
              label="All visit dates"
              multiple={true}
              options={visitDateOptions}
              searchable={visitDateOptions.length > 5}
              selectedLabel={(value) =>
                getPatientDocumentSelectedFilterLabel(value, visitDateOptions)
              }
              value={visitDateFilter.length > 0 ? visitDateFilter : null}
              onChange={(value) => setVisitDateFilter(value ?? [])}
            />
          </EntityProfileFilterBarItem>
          <EntityProfileFilterBarItem flex="0 0 190px" label="Submitted by" maxW="190px">
            <SharedSelect
              buttonProps={patientDocumentFilterSelectButtonProps}
              label="All submitters"
              multiple={true}
              options={submittedByOptions}
              searchable={submittedByOptions.length > 5}
              selectedLabel={(value) =>
                getPatientDocumentSelectedFilterLabel(value, submittedByOptions)
              }
              value={submittedByFilter.length > 0 ? submittedByFilter : null}
              onChange={(value) => setSubmittedByFilter(value ?? [])}
            />
          </EntityProfileFilterBarItem>
          <EntityProfileFilterBarItem flex="0 0 190px" label="Status" maxW="190px">
            <SharedSelect
              buttonProps={patientDocumentFilterSelectButtonProps}
              label="All statuses"
              multiple={true}
              options={statusOptions}
              searchable={statusOptions.length > 5}
              selectedLabel={(value) => getPatientDocumentSelectedFilterLabel(value, statusOptions)}
              value={statusFilter.length > 0 ? statusFilter : null}
              onChange={(value) => setStatusFilter(value ?? [])}
            />
          </EntityProfileFilterBarItem>
          <EntityProfileFilterBarItem flex="0 0 210px" label="Certification Period" maxW="210px">
            <SharedSelect
              buttonProps={patientDocumentFilterSelectButtonProps}
              label="All periods"
              multiple={true}
              options={certificationPeriodOptions}
              searchable={certificationPeriodOptions.length > 5}
              selectedLabel={(value) =>
                getPatientDocumentSelectedFilterLabel(value, certificationPeriodOptions)
              }
              value={certificationPeriodFilter.length > 0 ? certificationPeriodFilter : null}
              onChange={(value) => setCertificationPeriodFilter(value ?? [])}
            />
          </EntityProfileFilterBarItem>
        </EntityProfileFilterBar>

        <EntityProfileDataTable
          columns={columns}
          defaultPageSize={10}
          emptyLabel={null}
          getRowKey={(document, index) => `${document.__typename}-${document.id ?? index}`}
          rows={filteredDocuments}
          showEmptyState={false}
        />
      </VStack>
    </EntityProfileQuerySection>
  );
}

function PatientProfileBloodPressureSection(props: { patientId: PatientProfilePatient["id"] }) {
  const [dateRange, setDateRange] = useState<[LocalDate | null, LocalDate | null]>(() => [
    LocalDate.now().minusMonths(1),
    LocalDate.now(),
  ]);
  const bloodPressureQuery = usePatientBloodPressure(props.patientId, dateRange[0], dateRange[1]);
  const assignDeviceMutation = useAssignPatientBloodPressureDevice(props.patientId);
  const unassignDeviceMutation = useUnassignPatientBloodPressureDevice(props.patientId);
  const history = bloodPressureQuery.data?.history ?? [];
  const currentImei = bloodPressureQuery.data?.imei ?? null;
  const [isEditingDevice, setIsEditingDevice] = useState(false);
  const [draftImei, setDraftImei] = useState("");
  const [isDraftImeiValid, setIsDraftImeiValid] = useState(true);
  const isSavingDevice = assignDeviceMutation.isPending || unassignDeviceMutation.isPending;

  useEffect(() => {
    if (!isEditingDevice) {
      setDraftImei(currentImei ?? "");
      setIsDraftImeiValid(true);
    }
  }, [currentImei, isEditingDevice]);

  const columns = useMemo(
    (): Array<EntityProfileDataTableColumn<typeof history[number], BloodPressureSortKey>> => [
      {
        key: "doneAt",
        header: "Done At",
        render: (reading) => formatBloodPressureDoneAt(reading.done_at),
        sortKey: "doneAt",
        sortValue: (reading) => getBloodPressureSortDate(reading.done_at),
      },
      {
        key: "bloodPressure",
        header: "Blood Pressure",
        render: (reading) => `${reading.systolic}/${reading.diastolic}`,
        sortKey: "bloodPressure",
        sortValue: (reading) => `${reading.systolic}/${reading.diastolic}`,
      },
      {
        key: "pulse",
        header: "Pulse",
        render: (reading) => formatPatientProfileValue(reading.pulse),
        sortKey: "pulse",
        sortValue: (reading) => Number(reading.pulse ?? 0),
      },
      {
        key: "irregularHeartbeat",
        header: "Irregular Heartbeat",
        render: (reading) => formatBloodPressureBoolean(reading.irregular_heartbeat),
        sortKey: "irregularHeartbeat",
        sortValue: (reading) => (reading.irregular_heartbeat ? 1 : 0),
      },
      {
        key: "handTremor",
        header: "Hand Tremor",
        render: (reading) => formatBloodPressureBoolean(reading.hand_tremor),
        sortKey: "handTremor",
        sortValue: (reading) => (reading.hand_tremor ? 1 : 0),
      },
    ],
    []
  );

  const handleDateRangeChange = useCallback((value: [LocalDate, LocalDate | null] | null) => {
    setDateRange(value ?? [null, null]);
  }, []);

  const startEditingDevice = () => {
    setDraftImei(currentImei ?? "");
    setIsDraftImeiValid(true);
    setIsEditingDevice(true);
  };

  const saveDevice = () => {
    const trimmedImei = draftImei.trim();

    if (!/^[0-9]{15}$/.test(trimmedImei)) {
      setIsDraftImeiValid(false);
      return;
    }

    if (trimmedImei === currentImei) {
      setIsEditingDevice(false);
      return;
    }

    assignDeviceMutation.mutate(trimmedImei, {
      onSuccess: () => setIsEditingDevice(false),
    });
  };

  const unassignDevice = () => {
    unassignDeviceMutation.mutate(undefined, {
      onSuccess: () => setIsEditingDevice(false),
    });
  };

  return (
    <EntityProfileQuerySection
      errorLabel="Could not load blood pressure history."
      isError={bloodPressureQuery.isError}
      isLoading={bloodPressureQuery.isLoading}
      loadingLabel="Loading blood pressure history"
      onRetry={() => void bloodPressureQuery.refetch()}
    >
      <VStack alignItems="stretch" spacing={4}>
        <Stack
          alignItems={{ base: "stretch", md: "center" }}
          direction={{ base: "column", md: "row" }}
          justifyContent="space-between"
          spacing={3}
        >
          <HStack alignItems="center" flex="1" minW={0} spacing={2}>
            <Text color={entityProfileModalColors.title} fontSize="14px">
              Blood Pressure Device IMEI:
            </Text>
            {isEditingDevice ? (
              <Input
                borderColor={isDraftImeiValid ? undefined : "#ff0000"}
                maxLength={15}
                maxW={{ base: "100%", md: "25%" }}
                minW={{ base: "100%", md: "220px" }}
                size="sm"
                value={draftImei}
                onChange={(event) => {
                  setDraftImei(event.target.value);
                  setIsDraftImeiValid(true);
                }}
              />
            ) : (
              <Text color={entityProfileModalColors.text} fontSize="14px">
                {currentImei ?? "None"}
              </Text>
            )}
          </HStack>
          {isEditingDevice ? (
            <HStack justifyContent="flex-end" spacing={2}>
              {currentImei !== null ? (
                <EntityModalButton
                  disabled={isSavingDevice}
                  iconClassName="fa fa-trash"
                  isIconOnly={true}
                  kind="red"
                  onClick={unassignDevice}
                />
              ) : null}
              <EntityModalButton disabled={isSavingDevice} kind="blue" onClick={saveDevice}>
                Done
              </EntityModalButton>
            </HStack>
          ) : (
            <HStack justifyContent="flex-end">
              <EntityModalButton
                iconClassName="fa fa-edit"
                kind="blue"
                onClick={startEditingDevice}
              >
                Edit
              </EntityModalButton>
            </HStack>
          )}
        </Stack>

        <FormControl maxW={{ base: "100%", md: "300px" }}>
          <InputGroup>
            <RangeDatePicker
              dateFormat="MM/dd/yyyy"
              endDate={dateRange[1]}
              inputProps={{ h: "34px", fontSize: "14px", width: "full" }}
              selected={dateRange[1] ?? dateRange[0]}
              startDate={dateRange[0]}
              onChange={handleDateRangeChange}
            />
            <InputRightElement h="34px">
              <CalendarIcon color={entityProfileModalColors.muted} />
            </InputRightElement>
          </InputGroup>
        </FormControl>

        {history.length === 0 ? (
          <Box color="#53606c" fontSize="20px" fontWeight={600} my="48px" textAlign="center">
            Nothing to show
          </Box>
        ) : (
          <EntityProfileDataTable
            columns={columns}
            defaultPageSize={10}
            defaultSort={{ key: "doneAt", direction: "desc" }}
            getRowKey={(reading, index) => `${reading.done_at}-${index}`}
            pageSizeOptions={[10, 25, 50, 100]}
            rows={history}
          />
        )}
      </VStack>
    </EntityProfileQuerySection>
  );
}

function PatientProfileMedicalDiagnosisCodesSection(props: {
  patientId: PatientProfilePatient["id"];
}) {
  return <MedicalDiagnosisCodeTable patientId={props.patientId} />;
}

export default function PatientProfileMedicalSection(props: Props) {
  const authState = auth.getAuthState();
  const isOnboardingAgency =
    authState.type === "authenticated" && authState.data.agency.id === env.MEDFLYT_AGENCY_ID;
  const medicalSummaryProfile = props.medicalSummaryProfile ?? props.patient;
  const medicalInformationProfile = props.medicalInformationProfile ?? props.patient;
  const advancedDirectiveProfile = props.advancedDirectiveProfile ?? props.patient;

  switch (props.sectionId) {
    case "medical-summary":
      return medicalSummaryProfile === undefined ? (
        <ReadOnlyPlaceholder label="Start of care is unavailable." title="Medical summary" />
      ) : (
        <PatientProfileCertificationPeriodsSection
          actions={props.actions}
          patient={props.patient}
          patientId={props.patientId}
          profile={medicalSummaryProfile}
        />
      );
    case "medical-information":
      return medicalInformationProfile === undefined ? (
        <ReadOnlyPlaceholder
          label="Medical information profile fields are unavailable."
          title="Medical information"
        />
      ) : (
        <PatientProfileMedicalInformationSection
          actions={props.actions}
          patient={props.patient}
          patientId={props.patientId}
          profile={medicalInformationProfile}
        />
      );
    case "plan-of-care":
      return (
        <PatientProfilePlanOfCareSection
          actions={props.actions}
          patient={props.patient}
          patientId={props.patientId}
        />
      );
    case "duty-sheet":
      return (
        <PatientProfileDutySheetSection
          actions={props.actions}
          patient={props.patient}
          patientId={props.patientId}
        />
      );
    case "physicians":
      return (
        <PatientProfilePhysiciansSection
          actions={props.actions}
          patient={props.patient}
          patientId={props.patientId}
        />
      );
    case "medication-profile":
      return (
        <PatientProfileMedicationProfileSection
          actions={props.actions}
          patient={props.patient}
          patientId={props.patientId}
        />
      );
    case "tasks":
      return (
        <VStack alignItems="stretch" spacing={4}>
          <SectionActions>
            <PatientProfileMedicalBridgeButton
              actions={props.actions}
              label="Open actions"
              patient={props.patient}
              patientId={props.patientId}
              sectionId={props.sectionId}
              title="Tasks"
            />
          </SectionActions>
          <PatientProfileTasksSection patientId={props.patientId} />
        </VStack>
      );
    case "documents":
      return (
        <PatientProfileDocumentsSection
          actions={props.actions}
          patient={props.patient}
          patientId={props.patientId}
        />
      );
    case "blood-pressure":
      return <PatientProfileBloodPressureSection patientId={props.patientId} />;
    case "medical-diagnosis-codes":
      return <PatientProfileMedicalDiagnosisCodesSection patientId={props.patientId} />;
    case "advanced-directives":
      return isOnboardingAgency && advancedDirectiveProfile !== undefined ? (
        <PatientProfileAdvancedDirectivesSection
          patientId={props.patientId}
          profile={advancedDirectiveProfile}
        />
      ) : (
        <ReadOnlyPlaceholder
          label={
            isOnboardingAgency
              ? "Advanced directive profile fields are unavailable."
              : "Angular only shows Advanced Directives for the Medflyt onboarding agency."
          }
          title="Advanced directives unavailable"
        />
      );
    case "uploaded-documents":
      return (
        <PatientProfileUploadedDocumentsSection
          actions={props.actions}
          patientId={props.patientId}
        />
      );
    case "vbp":
      return (
        <PatientProfileVbpSection
          actions={props.actions}
          patient={props.patient}
          patientId={props.patientId}
        />
      );
    default:
      return (
        <PatientProfileMedicalBridgeOnlySection
          actions={props.actions}
          patient={props.patient}
          patientId={props.patientId}
          sectionId={props.sectionId}
          title="Medical"
        />
      );
  }
}
